import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Mic, MicOff, ArrowRight, ArrowUp, BookOpen,
  Sparkles, ChevronRight, Check, Coffee,
  Loader2, Volume2, X, Headphones, Pencil, Trash2, Menu, Plus, Feather
} from 'lucide-react';
import { toast } from 'sonner';
import { classifyTurnEnd } from '@/lib/turnDetection';
import { useVoiceFeatures } from '@/components/VoiceControls';
import storytellerMark from '@/assets/storyteller-mark.png';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const isIOSDevice = () => (
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
);

const isIOSChrome = () => isIOSDevice() && /CriOS/i.test(navigator.userAgent);

const hasActiveUserGesture = () => navigator.userActivation?.isActive ?? true;
// Turn-taking delays. We do NOT time the silence — Deepgram already reports
// end-of-speech. We look at *what was said* to decide whether the speaker is
// actually finished, because a pause mid-thought and a pause at the end of a
// thought are the same silence but need opposite handling.
const CONVERSATION_FINISHED_SEND_DELAY_MS = 300;   // sentence sounds complete — go
const CONVERSATION_UNFINISHED_SEND_DELAY_MS = 2600; // they're mid-thought — wait
const CONVERSATION_TERSE_SEND_DELAY_MS = 1400;      // very short reply, may continue
const CONVERSATION_TRANSCRIBED_SEND_DELAY_MS = 500;

const readInterviewResponseStream = async (body, onToken, onFinal) => {
  const token = localStorage.getItem('auth_token');
  const response = await fetch(`${API}/interview/respond`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    throw new Error(`Interview response failed: ${response.status}`);
  }
  if (!response.body) {
    throw new Error('Streaming is not supported by this browser');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let rawResponse = '';
  let finalPayload = null;

  const processEvent = (rawEvent) => {
    if (!rawEvent.trim()) return;
    const lines = rawEvent.split('\n');
    const event = lines.find(line => line.startsWith('event: '))?.slice(7).trim();
    const data = lines
      .filter(line => line.startsWith('data: '))
      .map(line => line.slice(6))
      .join('\n');
    if (!event || !data) return;

    const payload = JSON.parse(data);
    if (event === 'token') {
      onToken(payload.text || '');
    } else if (event === 'final') {
      finalPayload = payload;
      onFinal?.(payload);
    } else if (event === 'error') {
      throw new Error(payload.detail || 'Interview response failed');
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    const chunk = decoder.decode(value, { stream: true });
    rawResponse += chunk;
    buffer += chunk;
    const events = buffer.split('\n\n');
    buffer = events.pop() || '';
    events.forEach(processEvent);
  }

  if (buffer.trim()) {
    processEvent(buffer);
  }
  if (!finalPayload) {
    try {
      return JSON.parse(rawResponse);
    } catch {
      throw new Error('Interview response ended before final payload');
    }
  }
  return finalPayload;
};

const INTERVIEW_GENRES = [
  {
    value: 'memoir',
    label: 'Life Story',
    desc: 'Memories and experiences from your real life',
    chapter1Label: 'The Early Years',
    chapter1Desc: "We'll explore where you grew up, your family, and the moments that shaped you — the roots of your story.",
  },
  {
    value: 'fiction',
    label: 'Fiction',
    desc: 'A story from your imagination',
    chapter1Label: 'The World & Characters',
    chapter1Desc: "We'll introduce your main character and the world they live in.",
  },
  {
    value: 'business',
    label: 'Business Book',
    desc: 'Your expertise, journey, and leadership lessons',
    chapter1Label: 'How It Started',
    chapter1Desc: "We'll explore your origin story — what led you here and what those early days taught you.",
  },
  {
    value: 'childrens',
    label: "Children's Book",
    desc: 'A story for young readers',
    chapter1Label: 'Meet the Hero',
    chapter1Desc: "We'll get to know your main character — who they are and what their world looks like.",
  },
  {
    value: 'shortstory',
    label: 'Short Story',
    desc: 'A compact, powerful tale',
    chapter1Label: 'Setting the Scene',
    chapter1Desc: "We'll establish who's here, where we are, and what's about to unfold.",
  },
  {
    value: 'freestyle',
    label: 'Freestyle',
    desc: 'Write it your way — no rules',
    chapter1Label: 'Chapter One',
    chapter1Desc: "There are no rules. Start wherever feels right to you.",
  },
];

const getInterviewGenreConfig = (value) => {
  const normalized = value === 'lifestory' ? 'memoir' : value;
  return INTERVIEW_GENRES.find((g) => g.value === normalized) || INTERVIEW_GENRES[0];
};

const INTERVIEW_SECTION_ARCS = {
  memoir: [
    { key: 'early_life', label: 'Early Life' },
    { key: 'growing_up', label: 'Growing Up' },
    { key: 'finding_path', label: 'Finding Your Path' },
    { key: 'love_family', label: 'Love & Family' },
    { key: 'wisdom', label: 'Wisdom & Legacy' },
  ],
  lifestory: [
    { key: 'early_life', label: 'Early Life' },
    { key: 'growing_up', label: 'Growing Up' },
    { key: 'finding_path', label: 'Finding Your Path' },
    { key: 'love_family', label: 'Love & Family' },
    { key: 'wisdom', label: 'Wisdom & Legacy' },
  ],
  fiction: [
    { key: 'characters', label: 'Characters' },
    { key: 'world', label: 'The World' },
    { key: 'plot', label: 'The Plot' },
  ],
  shortstory: [
    { key: 'setup', label: 'Setting the Stage' },
    { key: 'conflict', label: 'The Conflict' },
    { key: 'resolution', label: 'The Resolution' },
  ],
  business: [
    { key: 'reader_problem', label: "The Reader's Problem" },
    { key: 'your_approach', label: 'Your Approach' },
    { key: 'credibility', label: 'Your Credibility' },
    { key: 'case_studies', label: 'Case Studies' },
    { key: 'transformation', label: 'The Transformation' },
  ],
  childrens: [
    { key: 'hero', label: 'The Hero' },
    { key: 'setting_world', label: 'The World' },
    { key: 'problem_adventure', label: 'The Adventure' },
    { key: 'lesson_heart', label: 'The Heart of the Story' },
    { key: 'ending_joy', label: 'The Ending' },
  ],
  freestyle: [
    { key: 'beginning', label: 'Beginning' },
    { key: 'development', label: 'Development' },
    { key: 'climax', label: 'Climax' },
    { key: 'resolution', label: 'Resolution' },
  ],
};

const getInterviewSections = (value) => {
  const normalized = (value || 'memoir').toLowerCase();
  return INTERVIEW_SECTION_ARCS[normalized] || INTERVIEW_SECTION_ARCS.memoir;
};

const getInterviewSectionLabel = (section) => {
  const allSections = Object.values(INTERVIEW_SECTION_ARCS).flat();
  return allSections.find((item) => item.key === section)?.label || section?.replace(/_/g, ' ') || '';
};

function InterviewMode({ bookId, bookTitle, genre, onClose, onChapterCreated }) {
  const navigate = useNavigate();
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);
  const initializedRef = useRef(false); // Track if session has been initialized
  const initialSections = getInterviewSections(genre);
  
  // Session state
  const [sessionId, setSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [isCompiling, setIsCompiling] = useState(false);
  const [compilationError, setCompilationError] = useState(null);
  const [isAiThinking, setIsAiThinking] = useState(false); // AI thinking state
  
  // Conversation state
  const [messages, setMessages] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [progress, setProgress] = useState(null);
  const [interviewGenre, setInterviewGenre] = useState(genre || 'memoir');
  const [isComplete, setIsComplete] = useState(false);
  const [sectionsToCompile, setSectionsToCompile] = useState([]);
  const [canCompileNow, setCanCompileNow] = useState(false); // Track if enough content for chapter
  
  // Typing animation state
  const [typingMessage, setTypingMessage] = useState(null);
  const [displayedText, setDisplayedText] = useState('');
  
  // Progress tracking
  const [chapterProgress, setChapterProgress] = useState({
    currentSection: initialSections[0]?.key || 'early_life',
    answersInSection: 0,
    totalSections: initialSections.length,
    currentSectionIndex: 0,
    estimatedWords: 0
  });
  
  // Skip nudge state
  const [showSkipNudge, setShowSkipNudge] = useState(false);
  const [missingElements, setMissingElements] = useState([]);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const [showComposerMenu, setShowComposerMenu] = useState(false);
  const [viewportFrame, setViewportFrame] = useState(() => ({
    top: 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0
  }));

  // Compilation style state
  const [showStyleModal, setShowStyleModal] = useState(false);
  const [sectionToCompile, setSectionToCompile] = useState(null);
  const [selectedStyle, setSelectedStyle] = useState('polished');
  const [compilationStyles] = useState([
    { id: 'as_spoke', name: 'As I Spoke', description: 'Keep my natural voice, minimal polish' },
    { id: 'polished', name: 'Polished Memoir', description: 'Professional, like a published book' },
    { id: 'warm', name: 'Warm & Personal', description: 'Intimate, emotional, heartfelt' },
    { id: 'simple', name: 'Simple & Clear', description: 'Straightforward, easy to read' },
  ]);

  // Onboarding state (shown before first interview session)
  const [onboardingStep, setOnboardingStep] = useState(null); // 'intro' | 'preview' | null
  const [onboardingGenre, setOnboardingGenre] = useState(null);
  const [onboardingVisible, setOnboardingVisible] = useState(false);
  
  // Voice features
  const {
    isListening,
    isTranscribing,
    startWhisperListening,
    stopWhisperListening,
    prepareVoiceInput,
    primeAudio,
    startDeepgramListening,
    stopDeepgramListening,
    speak,
    stopSpeaking,
    selectedVoice,
    interimTranscript,
    audioLevel,
    isRecordedMode
  } = useVoiceFeatures();

  const startPreferredVoiceInput = () => {
    if (conversationModeRef.current && isIOSChrome()) {
      markConversationTiming('ios_chrome_recorded_mode_start');
      return startWhisperListening(appendVoiceTranscript, { autoStop: true });
    }
    return startDeepgramListening(appendVoiceTranscript, { whisperAutoStop: conversationModeRef.current });
  };

  const stopAllVoiceInput = () => {
    stopDeepgramListening();
    stopWhisperListening();
  };

  // Compiled chapters viewer
  const [compiledChapters, setCompiledChapters] = useState([]);
  const [showChaptersModal, setShowChaptersModal] = useState(false);
  const [chaptersLoading, setChaptersLoading] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState(null);

  // Draft preview
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isPromoting, setIsPromoting] = useState(false);
  const [showStaleModal, setShowStaleModal] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const savedTimerRef = useRef(null);

  // Conversation mode state
  const [conversationMode, setConversationMode] = useState(false);
  const [isPromptSpeaking, setIsPromptSpeaking] = useState(false);
  const wasSpeakingRef = useRef(false);
  const lastAutoSpokenQuestionRef = useRef(null);
  const conversationModeRef = useRef(conversationMode);
  const currentAnswerRef = useRef('');
  const isListeningRef = useRef(isListening);
  const isCompleteRef = useRef(isComplete);
  const isSendingRef = useRef(false);
  const isTranscribingRef = useRef(isTranscribing);
  const isPromptSpeakingRef = useRef(false);
  const promptSpeechRunRef = useRef(0);
  // Tail of a reply whose opening sentence is already being spoken.
  const pendingSpeechRemainderRef = useRef('');
  const stopSpeakingRef = useRef(stopSpeaking);
  const handleSendAnswerRef = useRef(null);
  const liveUserMessageIdRef = useRef(null);
  const conversationAutoSendTimerRef = useRef(null);
  const interviewToastGuardRef = useRef({});
  const conversationTurnTraceRef = useRef(null);
  const awaitingBackendFinalBeforeMicRef = useRef(false);

  const markConversationTiming = (label, details = {}) => {
    if (!conversationModeRef.current) return;
    const now = performance.now();
    if (!conversationTurnTraceRef.current) {
      conversationTurnTraceRef.current = { startedAt: now };
    }
    const elapsedMs = Math.round(now - conversationTurnTraceRef.current.startedAt);
    console.log('[ConversationTiming]', label, { elapsedMs, ...details });
  };

  const notifyInterview = (key, message, type = 'info', ttl = 4500) => {
    const now = Date.now();
    if (interviewToastGuardRef.current[key] && now - interviewToastGuardRef.current[key] < ttl) return;
    interviewToastGuardRef.current[key] = now;
    const notifyFn = toast[type] || toast;
    notifyFn(message, { id: 'interview-status' });
  };

  const cancelConversationAutoSend = useCallback(() => {
    if (conversationAutoSendTimerRef.current) {
      clearTimeout(conversationAutoSendTimerRef.current);
      conversationAutoSendTimerRef.current = null;
    }
  }, []);

  const scheduleConversationAutoSend = useCallback((delay = CONVERSATION_TERSE_SEND_DELAY_MS) => {
    cancelConversationAutoSend();
    if (!conversationModeRef.current || isCompleteRef.current) return;

    conversationAutoSendTimerRef.current = setTimeout(() => {
      conversationAutoSendTimerRef.current = null;
      if (!conversationModeRef.current || isCompleteRef.current || isPromptSpeakingRef.current) return;
      if (isSendingRef.current) {
        scheduleConversationAutoSend(350);
        return;
      }
      if (isTranscribingRef.current) {
        scheduleConversationAutoSend(350);
        return;
      }
      const answer = currentAnswerRef.current.trim();
      if (!answer) return;
      markConversationTiming('auto_send_fire', { chars: answer.length });
      handleSendAnswerRef.current?.(answer);
    }, delay);
  }, [cancelConversationAutoSend]);

  const getConversationAutoSendDelay = (answer, recordedMode = false) => {
    // Recorded fallback has no live transcript to read, so timing is all we have.
    if (recordedMode) return CONVERSATION_TRANSCRIBED_SEND_DELAY_MS;

    switch (classifyTurnEnd(answer)) {
      case 'unfinished':
        return CONVERSATION_UNFINISHED_SEND_DELAY_MS;
      case 'uncertain':
        return CONVERSATION_TERSE_SEND_DELAY_MS;
      default:
        return CONVERSATION_FINISHED_SEND_DELAY_MS;
    }
  };

  useEffect(() => {
    stopSpeakingRef.current = stopSpeaking;
  }, [stopSpeaking]);

  const resizeAnswerInputSoon = () => {
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 180) + 'px';
        textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
      }
    }, 50);
  };

  const stopPromptSpeech = useCallback(() => {
    promptSpeechRunRef.current += 1;
    pendingSpeechRemainderRef.current = '';
    stopSpeakingRef.current?.();
    isPromptSpeakingRef.current = false;
    setIsPromptSpeaking(false);
  }, []);

  // Cut the AI off mid-sentence and hand the turn straight back to the speaker.
  // Nothing is lost — the full reply is already on screen as text.
  const handleInterruptSpeech = useCallback(() => {
    if (!isPromptSpeakingRef.current) return;
    markConversationTiming('tts_interrupted_by_user');
    awaitingBackendFinalBeforeMicRef.current = false;
    stopPromptSpeech();
    if (conversationModeRef.current && !isCompleteRef.current) {
      setTimeout(() => startConversationMic(), 50);
    }
  }, [stopPromptSpeech]); // eslint-disable-line react-hooks/exhaustive-deps

  const speakPrompt = (text, options = {}) => {
    const questionText = text?.trim();
    if (!questionText) return;

    // A continuation is the tail of a reply whose first sentence is already
    // playing. It must not reset speech state — that would flip isPromptSpeaking
    // to false and let the mic start on top of our own voice.
    if (!options.isContinuation) stopPromptSpeech();
    const runId = promptSpeechRunRef.current;
    const useBrowserTTS = options.forceBrowserTTS || (isIOSDevice() && !hasActiveUserGesture());

    speak(questionText, {
      voice: selectedVoice,
      speed: 1,
      forceStream: conversationModeRef.current,
      preferBrowserTTS: useBrowserTTS,
      suppressAutoplayError: isIOSDevice(),
      onStart: () => {
        if (runId !== promptSpeechRunRef.current) return;
        markConversationTiming('tts_play_start', { chars: questionText.length });
        isPromptSpeakingRef.current = true;
        setIsPromptSpeaking(true);
        options.onPlaybackStart?.();
        if (conversationModeRef.current && !isCompleteRef.current && !isIOSChrome()) {
          prepareVoiceInput()
            .then((ready) => markConversationTiming('mic_prepared_during_tts', { ready }))
            .catch((error) => markConversationTiming('mic_prepare_failed', { message: error?.message || String(error) }));
        }
      },
      onEnd: () => {
        if (runId !== promptSpeechRunRef.current) return;
        // If the rest of the reply arrived while the first sentence was playing,
        // speak it now and stay "speaking" so the mic doesn't cut in mid-reply.
        const remainder = pendingSpeechRemainderRef.current?.trim();
        if (remainder) {
          pendingSpeechRemainderRef.current = '';
          markConversationTiming('tts_continuation_start', { chars: remainder.length });
          speakPrompt(remainder, { isContinuation: true });
          return;
        }
        markConversationTiming('tts_play_end');
        isPromptSpeakingRef.current = false;
        setIsPromptSpeaking(false);
      },
      onError: (error) => {
        if (runId !== promptSpeechRunRef.current) return;
        markConversationTiming('tts_error', { message: error?.message || String(error) });
        isPromptSpeakingRef.current = false;
        setIsPromptSpeaking(false);
        options.onPlaybackError?.(error);
        if (isIOSDevice() && !useBrowserTTS) {
          setTimeout(() => speakPrompt(questionText, { forceBrowserTTS: true }), 0);
          return;
        }
        console.error('speakPrompt failed:', error);
        if (!options.suppressMicOnError && conversationModeRef.current && !isCompleteRef.current) {
          setTimeout(() => startConversationMic(), 150);
        }
      }
    });
  };

  // Stop speech when component unmounts (navigating away)
  useEffect(() => {
    return () => {
      cancelConversationAutoSend();
      stopPromptSpeech();
    };
  }, [cancelConversationAutoSend, stopPromptSpeech]);

  useEffect(() => {
    currentAnswerRef.current = currentAnswer;
  }, [currentAnswer]);

  useEffect(() => {
    conversationModeRef.current = conversationMode;
  }, [conversationMode]);

  useEffect(() => {
    isListeningRef.current = isListening;
  }, [isListening]);

  useEffect(() => {
    isCompleteRef.current = isComplete;
  }, [isComplete]);

  useEffect(() => {
    isSendingRef.current = isSending;
  }, [isSending]);

  useEffect(() => {
    isTranscribingRef.current = isTranscribing;
  }, [isTranscribing]);

  useEffect(() => {
    const viewport = window.visualViewport;

    const updateViewportFrame = () => {
      const nextTop = 0;
      const nextHeight = viewport
        ? Math.max(320, Math.round(viewport.height))
        : Math.max(320, window.innerHeight);

      setViewportFrame(prev => (
        prev.top === nextTop && prev.height === nextHeight
          ? prev
          : { top: nextTop, height: nextHeight }
      ));

      window.requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ block: 'end' });
      });
    };

    updateViewportFrame();
    viewport?.addEventListener('resize', updateViewportFrame);
    viewport?.addEventListener('scroll', updateViewportFrame);
    window.addEventListener('resize', updateViewportFrame);
    window.addEventListener('orientationchange', updateViewportFrame);

    return () => {
      viewport?.removeEventListener('resize', updateViewportFrame);
      viewport?.removeEventListener('scroll', updateViewportFrame);
      window.removeEventListener('resize', updateViewportFrame);
      window.removeEventListener('orientationchange', updateViewportFrame);
    };
  }, []);

  // When the page returns from background (iPhone screen-lock / app switch) with
  // conversation mode active, the Deepgram WS has closed and the AudioContext is
  // suspended. Auto-restarting silently leads to stuck "Listening…" UI. Instead,
  // stop all voice, clear sending state, and let the user tap the mic to re-engage.
  const [voicePausedByBackground, setVoicePausedByBackground] = useState(false);
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) return; // page going TO background — nothing to do
      if (!conversationModeRef.current) return;
      // Page just came back from background while conversation mode is on.
      // The mic/TTS session is broken; stop everything and prompt the user.
      cancelConversationAutoSend();
      stopPromptSpeech();
      stopAllVoiceInput();
      isListeningRef.current = false;
      awaitingBackendFinalBeforeMicRef.current = false;
      setVoicePausedByBackground(true);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [cancelConversationAutoSend, stopPromptSpeech]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear the "paused by background" flag whenever the user actively interacts.
  const clearVoicePausedByBackground = () => setVoicePausedByBackground(false);

  // Auto-save draft answer to localStorage
  useEffect(() => {
    if (bookId && currentAnswer.trim()) {
      localStorage.setItem(`interview_draft_${bookId}`, currentAnswer);
      setShowSaved(true);
      clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setShowSaved(false), 1000);
    }
  }, [currentAnswer, bookId]);

  // Load saved draft on mount
  useEffect(() => {
    if (bookId) {
      const savedDraft = localStorage.getItem(`interview_draft_${bookId}`);
      if (savedDraft && !currentAnswer) {
        setCurrentAnswer(savedDraft);
      }
    }
  }, [bookId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear draft after successful send
  const clearDraft = () => {
    if (bookId) {
      localStorage.removeItem(`interview_draft_${bookId}`);
    }
  };

  const upsertLiveUserMessage = useCallback((content) => {
    const cleanContent = content?.trim();
    if (!cleanContent) return;

    if (!liveUserMessageIdRef.current) {
      liveUserMessageIdRef.current = `live-user-${Date.now()}`;
      setMessages(prev => [...prev, {
        id: liveUserMessageIdRef.current,
        type: 'user',
        content: cleanContent
      }]);
      return;
    }

    setMessages(prev => prev.map(message => (
      message.id === liveUserMessageIdRef.current
        ? { ...message, content: cleanContent }
        : message
    )));
  }, []);

  const commitLiveUserMessage = useCallback((content) => {
    const cleanContent = content?.trim();
    if (!cleanContent) return false;

    if (!liveUserMessageIdRef.current) {
      liveUserMessageIdRef.current = `live-user-${Date.now()}`;
      setMessages(prev => [...prev, {
        id: liveUserMessageIdRef.current,
        type: 'user',
        content: cleanContent
      }]);
      return true;
    }

    setMessages(prev => prev.map(message => (
      message.id === liveUserMessageIdRef.current
        ? { ...message, content: cleanContent }
        : message
    )));
    return true;
  }, []);

  // Scroll to bottom when messages change or during streamed response
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, displayedText]);

  const appendVoiceTranscript = (transcript, options = {}) => {
    const cleanTranscript = transcript?.trim();
    if (!cleanTranscript) return;
    const next = currentAnswerRef.current
      ? `${currentAnswerRef.current} ${cleanTranscript}`.trim()
      : cleanTranscript;
    currentAnswerRef.current = next;
    setCurrentAnswer(next);
    commitLiveUserMessage(next);
    resizeAnswerInputSoon();
    if (conversationModeRef.current && !isCompleteRef.current) {
      const recordedMode = options.recordedMode === true || isRecordedMode;
      const sendDelay = getConversationAutoSendDelay(next, recordedMode);
      markConversationTiming('auto_send_scheduled', {
        delayMs: sendDelay,
        turnEnd: recordedMode ? 'recorded' : classifyTurnEnd(next),
        words: next.split(/\s+/).filter(Boolean).length,
        recordedMode
      });
      scheduleConversationAutoSend(sendDelay);
    }
  };

  const startConversationMic = () => {
    if (!conversationModeRef.current || isCompleteRef.current) return;
    if (awaitingBackendFinalBeforeMicRef.current) {
      markConversationTiming('mic_waiting_for_backend_final');
      setTimeout(() => startConversationMic(), 150);
      return;
    }
    if (isSendingRef.current) {
      setTimeout(() => startConversationMic(), 150);
      return;
    }
    if (isListeningRef.current) return; // already listening
    markConversationTiming('mic_start_requested');
    isListeningRef.current = true;
    const startResult = startPreferredVoiceInput();
    Promise.resolve(startResult).then((started) => {
      markConversationTiming('mic_start_result', { started });
      if (started === false) isListeningRef.current = false;
    }).catch((error) => {
      console.error('Failed to start conversation mic:', error);
      markConversationTiming('mic_start_failed', { message: error?.message || String(error) });
      isListeningRef.current = false;
    });
  };

  // When TTS finishes (true→false transition), auto-start mic in conversation mode.
  useEffect(() => {
    if (wasSpeakingRef.current && !isPromptSpeaking && conversationModeRef.current && !isCompleteRef.current) {
      startConversationMic();
    }
    wasSpeakingRef.current = isPromptSpeaking;
  }, [isPromptSpeaking]); // eslint-disable-line react-hooks/exhaustive-deps

  // Focus textarea and auto-speak each new question in voice mode.
  useEffect(() => {
    if (currentQuestion && textareaRef.current && !conversationModeRef.current) {
      textareaRef.current.focus();
    }
    const questionText = currentQuestion?.question?.trim();
    const questionKey = questionText ? `${currentQuestion.section || ''}:${questionText}` : null;
    if (questionText && conversationModeRef.current && questionKey !== lastAutoSpokenQuestionRef.current) {
      lastAutoSpokenQuestionRef.current = questionKey;
      if (isListeningRef.current) {
        isListeningRef.current = false;
        stopAllVoiceInput();
      }
      speakPrompt(questionText);
    }
  }, [currentQuestion]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (conversationMode && interimTranscript?.trim()) {
      cancelConversationAutoSend();
    }
  }, [conversationMode, interimTranscript, cancelConversationAutoSend]);

  useEffect(() => {
    if (!isListening || !interimTranscript?.trim()) return;
    const liveTranscript = currentAnswerRef.current
      ? `${currentAnswerRef.current} ${interimTranscript}`.trim()
      : interimTranscript.trim();
    upsertLiveUserMessage(liveTranscript);
  }, [isListening, interimTranscript, upsertLiveUserMessage]);

  // Resize textarea when interim transcript or committed answer changes
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 180) + 'px';
    }
  }, [currentAnswer, interimTranscript]);

  // Initialize interview session
  useEffect(() => {
    // Prevent double initialization in React Strict Mode
    if (initializedRef.current) return;
    initializedRef.current = true;
    initializeSession();
  }, [bookId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper function to get section index
  const getSectionIndex = (section) => {
    const sections = getInterviewSections(interviewGenre).map((item) => item.key);
    return Math.max(0, sections.indexOf(section));
  };

  // Calculate progress percentage for progress bar
  const calculateProgress = () => {
    const { currentSectionIndex, answersInSection, totalSections } = chapterProgress;
    const sectionWeight = 100 / totalSections;
    const answersNeeded = 5; // Assume 5 answers per section is "complete"
    const sectionProgress = Math.min(answersInSection / answersNeeded, 1) * sectionWeight;
    return Math.min((currentSectionIndex * sectionWeight) + sectionProgress, 100);
  };

  const initializeSession = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${API}/interview/start`,
        { book_id: bookId },
        
      );
      
      setSessionId(response.data.session_id);
      
      // Build initial messages
      const initialMessages = [];
      
      if (response.data.is_new) {
        // Welcome message
        initialMessages.push({
          type: 'system',
          content: response.data.welcome_message,
          icon: 'coffee'
        });
      } else {
        // Returning user - rebuild conversation history
        const compiledCount = response.data.progress?.chapters_compiled || 0;
        const totalAnswers = response.data.progress?.answers_count || 0;
        let welcomeContent;
        if (compiledCount > 0) {
          const chapterWord = compiledCount === 1 ? 'chapter' : 'chapters';
          welcomeContent = `Welcome back! You've completed ${compiledCount} ${chapterWord} so far. Let's keep building your story.`;
        } else if (totalAnswers > 0) {
          welcomeContent = `Welcome back! You've shared ${totalAnswers} memories so far. Let's continue where you left off.`;
        } else {
          welcomeContent = `Welcome back! Let's continue where you left off.`;
        }
        initialMessages.push({
          type: 'system',
          content: welcomeContent,
          icon: 'coffee'
        });
        
        // Rebuild previous Q&A from saved answers
        if (response.data.previous_answers && response.data.previous_answers.length > 0) {
          response.data.previous_answers.forEach((answer, answerIndex) => {
            // Add question (using ai-question type for consistent styling)
            initialMessages.push({
              type: 'ai-question',
              content: answer.question,
              section: answer.section,
              sectionLabel: answer.section_label || answer.section
            });
            // answerIndex is the position in the backend's stored answers array.
            // Edit/delete address that index directly rather than counting user
            // bubbles, which drifts by one whenever a send fails after the bubble
            // was already drawn.
            initialMessages.push({
              type: 'user',
              content: answer.answer,
              answerIndex
            });
          });
        }
      }
      
      setMessages(initialMessages);
      setCurrentQuestion(response.data.next_question);
      setProgress(response.data.progress);
      const activeGenre = response.data.genre || genre || 'memoir';
      const activeSections = getInterviewSections(activeGenre);
      const activeSection = response.data.next_question?.section || response.data.progress?.section || activeSections[0]?.key;
      const activeIndex = Math.max(0, activeSections.findIndex((item) => item.key === activeSection));
      setInterviewGenre(activeGenre);
      setChapterProgress({
        currentSection: activeSection,
        answersInSection: response.data.progress?.section_stats?.answers || 0,
        totalSections: activeSections.length,
        currentSectionIndex: activeIndex,
        estimatedWords: response.data.progress?.section_stats?.estimated_words || 0
      });
      const isOnboarded = localStorage.getItem(`interview_onboarded_${bookId}`);
      if (response.data.is_new && !isOnboarded) {
        setOnboardingGenre(getInterviewGenreConfig(response.data.genre || genre));
        setOnboardingStep('intro');
        setTimeout(() => setOnboardingVisible(true), 30);
      }
      if (response.data.can_compile || response.data.progress?.can_compile_section) {
        const readySections = response.data.sections_available?.length
          ? response.data.sections_available
          : [response.data.progress?.section].filter(Boolean);
        setSectionsToCompile(readySections);
        setCanCompileNow(readySections.length > 0);
      } else {
        setSectionsToCompile([]);
        setCanCompileNow(false);
      }
      
      // NOTE: Don't add next_question to messages here - it's displayed 
      // separately in the "Current Question" UI section below the messages
      
    } catch (error) {
      console.error('Failed to start interview:', error);
      toast.error('Failed to start interview session');
    } finally {
      setIsLoading(false);
      fetchCompiledChapters();
    }
  };

  const handleSendAnswer = async (answerOverride = null) => {
    cancelConversationAutoSend();
    const liveAnswer = interimTranscript
      ? `${currentAnswerRef.current}${currentAnswerRef.current ? ' ' : ''}${interimTranscript}`
      : currentAnswerRef.current;
    const answerSource = typeof answerOverride === 'string' ? answerOverride : liveAnswer;
    const answer = answerSource.trim();
    if (isSendingRef.current) return;

    if (!answer && isListeningRef.current) {
      if (isRecordedMode) {
        isListeningRef.current = false;
        stopWhisperListening();
        notifyInterview('transcribing-recording', 'Transcribing your recording...');
      } else {
        isListeningRef.current = false;
        stopAllVoiceInput();
      }
      return;
    }

    if (!answer) return;
    
    // Stop microphone when sending answer
    if (isListeningRef.current) {
      isListeningRef.current = false;
      stopAllVoiceInput();
    }
    
    setCurrentAnswer('');
    currentAnswerRef.current = '';
    if (textareaRef.current) textareaRef.current.style.height = '56px';
    clearDraft(); // Clear auto-saved draft
    setIsSending(true);
    isSendingRef.current = true;
    setIsAiThinking(true); // Show thinking indicator
    setCanCompileNow(false); // Reset compile state
    
    // Commit the live voice bubble (or create the user bubble for typed answers).
    commitLiveUserMessage(answer);
    // Hold on to the bubble's id: the ref is cleared immediately, but we need it
    // again once the backend tells us which stored answer this became.
    const committedMessageId = liveUserMessageIdRef.current;
    liveUserMessageIdRef.current = null;
    if (conversationModeRef.current) {
      conversationTurnTraceRef.current = { startedAt: performance.now() };
      markConversationTiming('answer_send_start', { chars: answer.length });
    }
    
    try {
      setTypingMessage({ type: 'ai', content: '', followUp: null });
      setDisplayedText('');

      // Start TTS as soon as the 'final' SSE event arrives, before the stream reader
      // loop finishes closing — saves 200-500 ms per conversation turn.
      let earlyTtsStarted = false;
      let earlyTtsAttempted = false;
      let firstTokenSeen = false;
      let streamedPromptForSpeech = '';
      // The opening sentence we already handed to TTS, so the `final` handler can
      // work out what still needs saying.
      let spokenPrefix = '';

      const responseData = await readInterviewResponseStream(
        { session_id: sessionId, answer },
        (token) => {
          if (!token) return;
          if (!firstTokenSeen) {
            firstTokenSeen = true;
            markConversationTiming('backend_first_token');
          }
          setIsAiThinking(false);
          setDisplayedText(prev => `${prev}${token}`);
          // Start speaking as soon as the first sentence lands, on every device.
          // Whatever is written after it is queued as a continuation (see the
          // `final` handler) so nothing gets swallowed.
          if (conversationModeRef.current && !isCompleteRef.current && !earlyTtsAttempted) {
            streamedPromptForSpeech += token;
            const sentenceEnd = streamedPromptForSpeech.search(/[.!?](\s|$)/);
            if (sentenceEnd >= 0) {
              const earlyPrompt = streamedPromptForSpeech.slice(0, sentenceEnd + 1).trim();
              if (earlyPrompt.length >= 16) {
                earlyTtsAttempted = true;
                spokenPrefix = earlyPrompt;
                awaitingBackendFinalBeforeMicRef.current = true;
                markConversationTiming('tts_early_from_stream', { chars: earlyPrompt.length });
                speakPrompt(earlyPrompt, {
                  suppressMicOnError: true,
                  onPlaybackStart: () => {
                    earlyTtsStarted = true;
                  },
                  onPlaybackError: () => {
                    earlyTtsAttempted = false;
                    earlyTtsStarted = false;
                    spokenPrefix = '';
                    pendingSpeechRemainderRef.current = '';
                    awaitingBackendFinalBeforeMicRef.current = false;
                  }
                });
              }
            }
          }
        },
        (payload) => {
          awaitingBackendFinalBeforeMicRef.current = false;
          if (!conversationModeRef.current || isCompleteRef.current) return;
          markConversationTiming('backend_final');

          const earlyPrompt = (
            payload.display_prompt ||
            payload.acknowledgment ||
            payload.next_question?.question ||
            ''
          ).trim();
          const earlyNextQ = payload.next_question
            ? { ...payload.next_question, question: earlyPrompt || payload.next_question.question }
            : null;
          if (earlyNextQ?.question) {
            lastAutoSpokenQuestionRef.current = `${earlyNextQ.section || ''}:${earlyNextQ.question}`;
          }

          if (earlyTtsStarted) {
            // The opening sentence went out while the model was still writing.
            // Say whatever came after it — otherwise the reply is cut short.
            const remainder = earlyPrompt && spokenPrefix && earlyPrompt.startsWith(spokenPrefix)
              ? earlyPrompt.slice(spokenPrefix.length).trim()
              : '';
            if (remainder) {
              markConversationTiming('tts_remainder_queued', { chars: remainder.length });
              if (isPromptSpeakingRef.current) {
                // Still mid-sentence — onEnd will pick this up.
                pendingSpeechRemainderRef.current = remainder;
              } else {
                // The opening sentence already finished. Speak the rest now,
                // before anything hands the turn back to the microphone.
                speakPrompt(remainder);
              }
              return;
            }
            // Nothing left to say. If speech is already done, take the turn.
            if (!isPromptSpeakingRef.current) {
              setTimeout(() => startConversationMic(), 50);
            }
            return;
          }

          if (!earlyPrompt) return;
          earlyTtsStarted = true;
          speakPrompt(earlyPrompt);
        }
      );
      const response = { data: responseData };
      
      // The answer is now stored; answers_count-1 is its index. Stamping it here
      // (rather than counting bubbles) means a failed send can never shift the
      // mapping and cause an edit to overwrite the wrong answer.
      const storedCount = response.data.progress?.answers_count;
      if (typeof storedCount === 'number' && committedMessageId) {
        setMessages(prev => prev.map(m => (
          m.id === committedMessageId ? { ...m, answerIndex: storedCount - 1 } : m
        )));
      }

      // Update progress tracking
      if (response.data.progress) {
        const sectionIndex = getSectionIndex(response.data.progress.section);
        const activeSections = getInterviewSections(interviewGenre);
        setChapterProgress({
          currentSection: response.data.progress.section,
          answersInSection: response.data.progress.section_stats?.answers || 0,
          totalSections: response.data.progress.total_sections || activeSections.length,
          currentSectionIndex: sectionIndex,
          estimatedWords: response.data.progress.section_stats?.estimated_words || 0
        });
      }

      // Store missing structural elements for skip nudge
      const missing = response.data.analysis_hint?.missing_structural_elements || [];
      setMissingElements(missing);

      const displayPrompt = (
        response.data.display_prompt ||
        response.data.acknowledgment ||
        response.data.next_question?.question ||
        ''
      ).trim();

      const nextQuestion = response.data.next_question
        ? { ...response.data.next_question, question: displayPrompt || response.data.next_question.question }
        : null;

      // Build the single AI prompt bubble. This exact text is what gets shown,
      // spoken aloud, stored as currentQuestion, and answered by the user.
      const typingPayload = {
        type: 'ai',
        content: displayPrompt,
        followUp: null,
        offersPause: response.data.offers_pause,
        pauseMessage: response.data.pause_message,
        userState: response.data.analysis_hint?.user_state,
        momentum: response.data.analysis_hint?.momentum,
        sceneLockActive: response.data.analysis_hint?.scene_lock_active
      };

      // Commit the streamed text; play voice in parallel in conversation mode.
      setIsAiThinking(false);
      setMessages(prev => [...prev, typingPayload]);
      setTypingMessage(null);
      setDisplayedText('');
      if (conversationModeRef.current && !isCompleteRef.current && displayPrompt && !earlyTtsStarted) {
        if (nextQuestion?.question) {
          lastAutoSpokenQuestionRef.current = `${nextQuestion.section || ''}:${nextQuestion.question}`;
        }
        speakPrompt(displayPrompt);
      }
      
      // Track if we have enough content for a chapter
      if (response.data.can_compile || response.data.progress?.can_compile_section) {
        setCanCompileNow(true);
        // Add the current section to compile list
        const currentSection = response.data.progress?.section || progress?.section;
        if (currentSection) {
          setSectionsToCompile(prev => [...new Set([...prev, currentSection])]);
        }
      }
      
      // Check if auto_compile was triggered (user said "yes" to compilation offer)
      if (response.data.auto_compile && response.data.compile_section) {
        // Auto-trigger compilation
        setTimeout(() => {
          setSectionToCompile(response.data.compile_section);
          setShowStyleModal(true);
        }, 1500);
        setIsSending(false);
        return; // Exit early - don't add next question
      }
      
      // Check if section changed
      if (response.data.section_complete) {
        // Add section intro for the new section
        if (response.data.section_intro) {
          setTimeout(() => {
            setMessages(prev => [...prev, {
              type: 'section-intro',
              content: response.data.section_intro,
              section: nextQuestion?.section,
              sectionLabel: nextQuestion?.section_label
            }]);
          }, 1000);
        }
        // Add section to compile list
        const completedSection = progress?.section || getInterviewSections(interviewGenre)[0]?.key || 'early_life';
        setSectionsToCompile(prev => [...new Set([...prev, completedSection])]);
      }
      
      // Show pause option if offered
      if (response.data.offers_pause && response.data.pause_message) {
        setTimeout(() => {
          setMessages(prev => [...prev, {
            type: 'pause-offer',
            content: response.data.pause_message,
            section: nextQuestion?.section
          }]);
        }, 500);
      }
      
      // Check if complete
      if (response.data.is_complete) {
        setIsComplete(true);
        setTimeout(() => {
          setMessages(prev => [...prev, {
            type: 'complete',
            content: response.data.message
          }]);
        }, 1500);
      } else {
        // Set current prompt state for read/replay, voice mode, and answer context.
        // The prompt itself is rendered once in the messages list.
        setCurrentQuestion(nextQuestion);
        setProgress(response.data.progress);
      }
      
    } catch (error) {
      awaitingBackendFinalBeforeMicRef.current = false;
      console.error('Failed to send answer:', error);
      toast.error('Could not send that yet. Please try again.', { id: 'interview-send-status' });
      setTypingMessage(null);
      setDisplayedText('');
      setIsAiThinking(false);
      // Re-enable mic in conversation mode so user can try again without manual tap
      if (conversationModeRef.current && !isCompleteRef.current) {
        setTimeout(() => startConversationMic(), 800);
      }
    } finally {
      setIsSending(false);
      isSendingRef.current = false;
      // Safety net: if conversation mode is on but neither TTS nor mic is active
      // 5 seconds after the send completes, something silently failed — restart mic.
      if (conversationModeRef.current && !isCompleteRef.current) {
        setTimeout(() => {
          if (
            conversationModeRef.current &&
            !isCompleteRef.current &&
            !isPromptSpeakingRef.current &&
            !isListeningRef.current &&
            !isSendingRef.current &&
            !isTranscribingRef.current
          ) {
            markConversationTiming('safety_net_mic_restart');
            startConversationMic();
          }
        }, 5000);
      }
    }
  };

  useEffect(() => {
    handleSendAnswerRef.current = handleSendAnswer;
  });

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendAnswer();
    }
  };

  const handleVoiceInput = () => {
    setShowComposerMenu(false);
    clearVoicePausedByBackground();
    if (isTranscribing) {
      notifyInterview('still-transcribing', 'Still transcribing your recording...');
      return;
    }

    if (isListeningRef.current) {
      isListeningRef.current = false;
      stopAllVoiceInput();
    } else {
      // Set ref eagerly so the isPromptSpeaking effect doesn't double-start the mic
      isListeningRef.current = true;
      stopPromptSpeech();
      const startResult = startPreferredVoiceInput();
      Promise.resolve(startResult).then((started) => {
        if (started === false) isListeningRef.current = false;
      }).catch((error) => {
        console.error('Failed to start voice input:', error);
        isListeningRef.current = false;
      });
    }
  };

  const handleReadQuestion = () => {
    if (isPromptSpeakingRef.current) {
      stopPromptSpeech();
    } else if (currentQuestion && currentQuestion.question && currentQuestion.question.trim()) {
      speakPrompt(currentQuestion.question);
    } else {
      toast.error('No question to read');
    }
  };

  const readQuestionAndThenListen = () => {
    if (!currentQuestion?.question?.trim()) return;
    speakPrompt(currentQuestion.question);
    // mic will auto-start via useEffect([isPromptSpeaking]) when audio ends
  };

  const handleContinueToNextChapter = (chapterMessage) => {
    const nextQuestion = chapterMessage?.nextQuestion;
    const questionText = nextQuestion?.question?.trim();

    if (!questionText) {
      toast.info('Preparing the next chapter question...');
      return;
    }

    setMessages(prev => {
      const additions = [];
      const introText = chapterMessage.sectionIntro?.trim();
      const section = nextQuestion.section || chapterMessage.section;
      const sectionLabel = nextQuestion.section_label || chapterMessage.sectionLabel;

      if (
        introText &&
        !prev.some(message => (
          message.type === 'section-intro' &&
          message.section === section &&
          message.content === introText
        ))
      ) {
        additions.push({
          type: 'section-intro',
          content: introText,
          section,
          sectionLabel
        });
      }

      if (
        !prev.some(message => (
          message.type === 'ai-question' &&
          message.section === section &&
          message.content === questionText
        ))
      ) {
        additions.push({
          type: 'ai-question',
          content: questionText,
          section,
          sectionLabel
        });
      }

      return additions.length ? [...prev, ...additions] : prev;
    });

    setCurrentQuestion(nextQuestion);
    if (nextQuestion.section) {
      setProgress(prev => ({
        ...prev,
        section: nextQuestion.section
      }));
    }

    window.setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      textareaRef.current?.focus();
    }, 80);

    if (conversationModeRef.current) {
      lastAutoSpokenQuestionRef.current = `${nextQuestion.section || ''}:${questionText}`;
      speakPrompt(questionText);
    }
  };

  // Corrections must reach the stored answer, not just the bubble on screen —
  // the chapter is written from the stored text.
  const handleEditAnswer = async (messageIdx, answerIndex, newContent) => {
    const previous = messages[messageIdx]?.content;
    setMessages(prev => prev.map((m, i) => (i === messageIdx ? { ...m, content: newContent } : m)));
    try {
      const res = await axios.patch(
        `${API}/interview/session/${sessionId}/answers/${answerIndex}`,
        { answer: newContent }
      );
      if (res.data?.invalidated_drafts > 0) {
        setPreviewContent(null);
        toast.info('Saved. Your draft was written from the old version — generate it again when you\'re ready.');
      } else {
        toast.success('Saved.');
      }
    } catch (err) {
      // Put the old text back rather than leaving the screen disagreeing with
      // what the book will actually be written from.
      setMessages(prev => prev.map((m, i) => (i === messageIdx ? { ...m, content: previous } : m)));
      toast.error(err?.response?.data?.detail || 'Could not save that change. Please try again.');
    }
  };

  const handleDeleteAnswer = async (messageIdx, answerIndex) => {
    const snapshot = messages;
    // Drop the bubble and shift every later index down, so the next edit still
    // addresses the right stored answer.
    setMessages(prev => prev
      .filter((_, i) => i !== messageIdx)
      .map(m => (m.answerIndex != null && m.answerIndex > answerIndex
        ? { ...m, answerIndex: m.answerIndex - 1 }
        : m)));
    try {
      const res = await axios.delete(`${API}/interview/session/${sessionId}/answers/${answerIndex}`);
      if (res.data?.invalidated_drafts > 0) setPreviewContent(null);
    } catch (err) {
      setMessages(snapshot);
      toast.error(err?.response?.data?.detail || 'Could not delete that. Please try again.');
    }
  };

  const handleVoiceModeToggle = async () => {
    const turningOn = !conversationModeRef.current;
    console.log('[ConversationVoice] user tapped conversation mode, turningOn:', turningOn);
    cancelConversationAutoSend();
    stopPromptSpeech();
    if (isListeningRef.current) {
      isListeningRef.current = false;
      stopAllVoiceInput();
    }

    if (!turningOn) {
      setConversationMode(false);
      conversationModeRef.current = false;
      stopAllVoiceInput();
      return;
    }

    // iOS requires audio.play() to be called synchronously inside the user gesture
    // stack. primeAudio() runs unlockAudio() here — before any await — so the silent
    // audio element is unlocked while we are still in the tap handler. All subsequent
    // TTS calls reuse the same element and iOS allows them even after async work.
    primeAudio();

    const micReady = isIOSChrome() ? true : await prepareVoiceInput();
    if (!micReady) {
      setConversationMode(false);
      conversationModeRef.current = false;
      return;
    }

    setConversationMode(true);
    conversationModeRef.current = true; // update ref synchronously so effects see it immediately
    if (turningOn) {
      textareaRef.current?.blur();
      readQuestionAndThenListen();
    }
  };

  const fetchCompiledChapters = async () => {
    if (!bookId) return;
    try {
      const res = await axios.get(`${API}/books/${bookId}/chapters`);
      setCompiledChapters((res.data || []).filter(c => c.compiled_from_interview));
    } catch {
      // silently ignore — not critical
    }
  };

  const openChaptersModal = async () => {
    setSelectedChapter(null);
    setShowChaptersModal(true);
    setChaptersLoading(true);
    try {
      const res = await axios.get(`${API}/books/${bookId}/chapters`);
      setCompiledChapters((res.data || []).filter(c => c.compiled_from_interview));
    } finally {
      setChaptersLoading(false);
    }
  };

  const handlePreviewDraft = async () => {
    const answerCount = progress?.section_stats?.answers ?? chapterProgress.answersInSection ?? 0;
    if (answerCount < 2) {
      // Not enough answers yet — open modal with a friendly nudge instead of calling the API
      setPreviewContent({ tooShort: true });
      setShowPreviewModal(true);
      return;
    }
    setPreviewContent(null);
    setShowPreviewModal(true);
    setIsPreviewLoading(true);
    try {
      const res = await axios.post(`${API}/interview/compile/preview`, { session_id: sessionId });
      setPreviewContent(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not generate preview. Try again.';
      toast.error(msg);
      setShowPreviewModal(false);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleRegenerateDraft = async () => {
    setPreviewContent(null);
    setIsPreviewLoading(true);
    setShowStaleModal(false);
    try {
      const res = await axios.post(`${API}/interview/compile/preview`, {
        session_id: sessionId,
        refresh: true
      });
      setPreviewContent(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not regenerate draft. Try again.';
      toast.error(msg);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handlePromoteDraft = async (force = false) => {
    if (!previewContent?.draft_id) return;

    const currentAnswerCount = chapterProgress.answersInSection ?? 0;
    const isStale = previewContent.answer_count != null &&
      currentAnswerCount > previewContent.answer_count;

    if (isStale && !force) {
      setShowStaleModal(true);
      return;
    }

    setIsPromoting(true);
    setShowStaleModal(false);
    try {
      const res = await axios.post(
        `${API}/interview/drafts/${previewContent.draft_id}/promote`,
        { book_id: bookId }
      );
      toast.success(`Chapter "${res.data.title}" saved!`);
      setShowPreviewModal(false);
      setPreviewContent(null);
      if (onChapterCreated) onChapterCreated(res.data.chapter_id);
      fetchCompiledChapters();

      try {
        const skipResponse = await axios.post(`${API}/interview/skip-section`, { session_id: sessionId });
        if (skipResponse.data.success && skipResponse.data.new_section) {
          const newSection = skipResponse.data.new_section;
          const sectionLabel = skipResponse.data.next_question?.section_label || getSectionLabel(newSection);
          setMessages(prev => [...prev, {
            type: 'system',
            content: `Moving on to: ${sectionLabel}`,
            icon: 'arrow-right'
          }]);
          if (skipResponse.data.next_question) {
            setCurrentQuestion(skipResponse.data.next_question);
          }
        }
      } catch (_) {}
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not save chapter. Try again.';
      toast.error(msg);
    } finally {
      setIsPromoting(false);
    }
  };

  const handleCompileChapter = async (section) => {
    // Show style selector modal
    setSectionToCompile(section);
    setShowStyleModal(true);
  };

  const confirmCompileChapter = async () => {
    if (!sectionToCompile) return;
    
    setIsCompiling(true);
    setCompilationError(null);
    setShowStyleModal(false);

    // Add compiling message
    setMessages(prev => [...prev, {
      type: 'system',
      content: 'Creating your chapter... This may take a minute.',
      icon: 'sparkles'
    }]);
    
    try {
      // Start the compilation job
      const startResponse = await axios.post(
        `${API}/interview/compile/start`,
        { 
          session_id: sessionId, 
          section: sectionToCompile,
          style: selectedStyle 
        },
        
      );
      
      const jobId = startResponse.data.job_id;
      
      // Poll for completion
      let attempts = 0;
      const maxAttempts = 150; // 5 minutes max (2s interval)
      
      const pollStatus = async () => {
        try {
          const statusResponse = await axios.get(
            `${API}/interview/compile/status/${jobId}`,
            
          );
          
          const job = statusResponse.data;
          
          if (job.status === 'completed') {
            toast.success(`Chapter "${job.result.title}" created!`);
            
            // Remove from compile list
            setSectionsToCompile(prev => prev.filter(s => s !== sectionToCompile));
            setCanCompileNow(false);
            
            // Add success message
            setMessages(prev => [...prev, {
              type: 'chapter-created',
              content: `Your "${job.result.title}" chapter is ready! ${job.result.word_count} words of your story.`,
              chapterId: job.result.chapter_id,
              nextQuestionLoading: true
            }]);
            
            if (onChapterCreated) {
              onChapterCreated(job.result.chapter_id);
            }
            fetchCompiledChapters();
            
            // Automatically move to the next section after chapter creation
            try {
              const skipResponse = await axios.post(
                `${API}/interview/skip-section`,
                { session_id: sessionId },
                
              );
              
              if (skipResponse.data.success && skipResponse.data.new_section) {
                const newSection = skipResponse.data.new_section;
                const sectionLabel = skipResponse.data.next_question?.section_label || getSectionLabel(newSection);
                const nextQuestion = skipResponse.data.next_question;

                setMessages(prev => prev.map(message => (
                  message.type === 'chapter-created' && message.chapterId === job.result.chapter_id
                    ? {
                        ...message,
                        nextQuestion,
                        nextQuestionLoading: false,
                        sectionIntro: skipResponse.data.section_intro,
                        section: newSection,
                        sectionLabel
                      }
                    : message
                )));
                
                // Add the section intro message for the next chapter
                setTimeout(() => {
                  // Update the current question to the new section
                  setCurrentQuestion(nextQuestion);
                  setProgress(prev => ({
                    ...prev,
                    section: newSection
                  }));
                }, 1500);
              }
            } catch (skipError) {
              setMessages(prev => prev.map(message => (
                message.type === 'chapter-created' && message.chapterId === job.result.chapter_id
                  ? { ...message, nextQuestionLoading: false }
                  : message
              )));
              // If skip fails, that's okay - user can continue manually
            }
            
            setSectionToCompile(null);
            setIsCompiling(false);
            return;
          } else if (job.status === 'failed') {
            const failMsg = 'Failed to create chapter. Please try again.';
            console.error('Compilation failed:', job.error);
            toast.error(failMsg);
            setCompilationError(failMsg);
            setIsCompiling(false);
            return;
          } else {
            // Still processing - continue polling
            attempts++;
            if (attempts < maxAttempts) {
              setTimeout(pollStatus, 2000); // Poll every 2 seconds
            } else {
              const timeoutMsg = 'Chapter creation timed out. Please try again.';
              toast.error(timeoutMsg);
              setCompilationError(timeoutMsg);
              setIsCompiling(false);
            }
          }
        } catch (pollError) {
          console.error('Poll error:', pollError);
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(pollStatus, 2000);
          } else {
            const pollErrMsg = 'Failed to get compilation status. Please try again.';
            toast.error(pollErrMsg);
            setCompilationError(pollErrMsg);
            setIsCompiling(false);
          }
        }
      };
      
      // Start polling
      setTimeout(pollStatus, 2000);
      
    } catch (error) {
      console.error('Failed to start chapter compilation:', error);
      const startErrMsg = error?.response?.data?.detail || 'Failed to start chapter creation. Please try again.';
      toast.error(startErrMsg);
      setCompilationError(startErrMsg);
      setIsCompiling(false);
    }
  };

  const handleSkipSection = async () => {
    try {
      const response = await axios.post(
        `${API}/interview/skip-section`,
        { session_id: sessionId },
        
      );
      
      if (response.data.success) {
        toast.info(response.data.message);
        
        // Add skip message
        setMessages(prev => [...prev, {
          type: 'section-skip',
          content: response.data.message,
          previousSection: response.data.previous_section
        }]);
        
        // Add previous section to compile list
        setSectionsToCompile(prev => [...new Set([...prev, response.data.previous_section])]);
        
        // Set next question
        setCurrentQuestion(response.data.next_question);
      } else {
        toast.warning(response.data.message);
      }
    } catch (error) {
      console.error('Failed to skip section:', error);
      toast.error('Unable to skip section right now.');
    }
  };

  const requestSkipSection = () => {
    setShowHeaderMenu(false);
    if (missingElements.length > 0 && chapterProgress.answersInSection < 7) {
      setShowSkipNudge(true);
    } else {
      handleSkipSection();
    }
  };

  const handleViewBook = () => {
    navigate(`/books/${bookId}`);
    if (onClose) onClose();
  };

  const handleStartInterview = () => {
    localStorage.setItem(`interview_onboarded_${bookId}`, '1');
    setOnboardingVisible(false);
    setTimeout(() => {
      setOnboardingStep(null);
    }, 500);
  };

  const handleShowPreview = () => {
    setOnboardingVisible(false);
    setTimeout(() => {
      setOnboardingStep('preview');
      setTimeout(() => setOnboardingVisible(true), 30);
    }, 300);
  };

  if (onboardingStep !== null) {
    return (
      <div
        className={`fixed inset-0 z-[70] bg-[#FBF8F2] dark:bg-[#1A1816] flex flex-col transition-opacity duration-500 ${
          onboardingVisible ? 'opacity-100' : 'opacity-0'
        }`}
      >

        {/* ── Step: intro ──────────────────────────────────────────────────── */}
        {onboardingStep === 'intro' && onboardingGenre && (
          <div className="flex-1 overflow-y-auto px-5 py-10">
            <div className="max-w-sm mx-auto">

              <div className="text-center mb-8">
                <span className="inline-block text-[10px] font-semibold tracking-[0.15em] uppercase px-3 py-1 rounded-full bg-[#E8DED0] dark:bg-[#2A2520] text-[#8B7355] dark:text-[#C4A882] mb-5">
                  {onboardingGenre.label}
                </span>
                <h1 className="font-serif text-2xl font-bold text-[#2A2420] dark:text-[#F0EBE4] mb-2">
                  Your story is ready to begin.
                </h1>
                <p className="text-[#2A2420]/55 dark:text-[#F0EBE4]/55 text-sm">
                  Here's what Chapter 1 will cover.
                </p>
              </div>

              <div className="bg-white/60 dark:bg-[#22201C]/60 rounded-2xl border border-[#E0D8CC] dark:border-[#2A2520] px-4 py-4 mb-6">
                <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#8B7355] dark:text-[#C4A882] mb-2">
                  Chapter 1 — {onboardingGenre.chapter1Label}
                </p>
                <p className="text-sm text-[#2A2420]/80 dark:text-[#F0EBE4]/80 leading-relaxed">
                  {onboardingGenre.chapter1Desc}
                </p>
              </div>

              <div className="mb-8 space-y-3">
                <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#2A2420]/40 dark:text-[#F0EBE4]/40 mb-3">
                  What to expect
                </p>
                {[
                  'Answer questions at your own pace',
                  'AI turns your answers into a real chapter',
                  'Edit, polish, and make it yours',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#8B7355]/15 dark:bg-[#C4A882]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[10px] font-bold text-[#8B7355] dark:text-[#C4A882]">{i + 1}</span>
                    </div>
                    <p className="text-sm text-[#2A2420]/70 dark:text-[#F0EBE4]/70 leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleStartInterview}
                  className="w-full bg-[#2A2420] dark:bg-[#F0EBE4] text-[#F5F1E8] dark:text-[#2A2420] font-semibold py-3.5 rounded-2xl hover:bg-[#1A1816] dark:hover:bg-white transition-colors flex items-center justify-center gap-2"
                >
                  Start writing
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={handleShowPreview}
                  className="w-full border border-[#2A2420]/20 dark:border-[#F0EBE4]/20 text-[#2A2420] dark:text-[#F0EBE4] font-medium py-3.5 rounded-2xl hover:bg-[#2A2420]/5 dark:hover:bg-[#F0EBE4]/5 transition-colors"
                >
                  See a sample chapter first
                </button>
              </div>

            </div>
          </div>
        )}

        {/* ── Step: preview ────────────────────────────────────────────────── */}
        {onboardingStep === 'preview' && (
          <div className="flex-1 overflow-y-auto px-5 py-8">
            <div className="max-w-sm mx-auto">

              <button
                onClick={() => { setOnboardingVisible(false); setTimeout(() => { setOnboardingStep('intro'); setTimeout(() => setOnboardingVisible(true), 30); }, 300); }}
                className="flex items-center gap-1.5 text-sm text-[#8B7355] dark:text-[#C4A882] mb-7 hover:opacity-70 transition-opacity"
              >
                <ArrowRight className="w-3.5 h-3.5 rotate-180" />
                Back
              </button>

              <div className="mb-6">
                <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#8B7355] dark:text-[#C4A882] mb-2">
                  Sample output
                </p>
                <h2 className="font-serif text-xl font-bold text-[#2A2420] dark:text-[#F0EBE4]">
                  Here's what your chapter could look like
                </h2>
              </div>

              <div className="bg-white/70 dark:bg-[#22201C]/70 rounded-2xl border border-[#E0D8CC] dark:border-[#2A2520] px-5 py-5 mb-3 shadow-sm">
                <p className="font-serif text-sm italic text-[#2A2420]/85 dark:text-[#F0EBE4]/85 leading-[1.8] mb-4">
                  The kitchen smelled like coffee and burnt toast — the same way it always had on Sunday mornings. I was seven years old, sitting at the table with my legs dangling, watching my father read the newspaper without really reading it.
                </p>
                <p className="font-serif text-sm italic text-[#2A2420]/85 dark:text-[#F0EBE4]/85 leading-[1.8] mb-4">
                  He hadn't said much since the night before. None of us had.
                </p>
                <p className="font-serif text-sm italic text-[#2A2420]/85 dark:text-[#F0EBE4]/85 leading-[1.8] mb-4">
                  "Are you nervous about school tomorrow?" my mother asked me, not looking up from the stove.
                </p>
                <p className="font-serif text-sm italic text-[#2A2420]/85 dark:text-[#F0EBE4]/85 leading-[1.8]">
                  I told her I wasn't. But I was.
                </p>
              </div>
              <p className="text-xs text-[#2A2420]/40 dark:text-[#F0EBE4]/40 text-center mb-8 leading-relaxed">
                Generated from real interview answers — your chapter will be completely unique.
              </p>

              <button
                onClick={handleStartInterview}
                className="w-full bg-[#2A2420] dark:bg-[#F0EBE4] text-[#F5F1E8] dark:text-[#2A2420] font-semibold py-3.5 rounded-2xl hover:bg-[#1A1816] dark:hover:bg-white transition-colors flex items-center justify-center gap-2"
              >
                I'm ready to start writing
                <ArrowRight className="w-4 h-4" />
              </button>

            </div>
          </div>
        )}


      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        className="story-page fixed left-0 right-0 flex items-center justify-center z-[60]"
        style={{
          top: `${viewportFrame.top}px`,
          height: `${viewportFrame.height}px`,
          maxHeight: `${viewportFrame.height}px`
        }}
      >
        <div className="text-center">
          <Coffee className="w-10 h-10 text-[var(--story-primary)] mx-auto mb-4 animate-pulse" />
          <p className="story-body text-base">Preparing your interview...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="story-page fixed left-0 right-0 flex flex-col z-[60] overflow-hidden"
      style={{
        top: `${viewportFrame.top}px`,
        height: `${viewportFrame.height}px`,
        maxHeight: `${viewportFrame.height}px`,
        overscrollBehavior: 'none'
      }}
    >
      {/* Header */}
      <header className="pointer-events-none absolute left-0 right-0 top-0 z-[70] px-4 pt-[calc(env(safe-area-inset-top,0px)+0.75rem)]">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-3">
          <div className="pointer-events-auto flex min-w-0 items-center gap-2 rounded-full border border-[var(--story-line)] bg-[var(--story-surface-solid)]/86 px-2 py-1.5 shadow-[var(--story-shadow-s)] backdrop-blur-xl dark:bg-[#12100E]/82">
            <img
              src={storytellerMark}
              alt="The Storyteller"
              className="h-8 w-8 rounded-full object-cover ring-1 ring-black/5 dark:ring-white/10"
            />
            <div className="min-w-0 pr-2">
              <h1 className="truncate text-sm font-semibold leading-tight text-[var(--story-ink)] sm:text-base">{bookTitle}</h1>
              <p className="truncate text-[11px] leading-tight text-[var(--story-muted)]">Sharing your story</p>
            </div>
          </div>
        
          <div className="pointer-events-auto flex items-center gap-2">
          {!isComplete && (
            <div className="hidden sm:flex items-center gap-1.5">
              {Array.from({ length: chapterProgress.totalSections }).map((_, idx) => (
                <span
                  key={idx}
                  className={`text-[8px] ${
                    idx < chapterProgress.currentSectionIndex
                      ? 'text-[#8B7355]'
                      : idx === chapterProgress.currentSectionIndex
                      ? 'text-stone-600 dark:text-stone-400'
                      : 'text-stone-300 dark:text-stone-700'
                  }`}
                >
                  •
                </span>
              ))}
            </div>
          )}

          <div className="relative">
            <Button
              type="button"
              onClick={() => setShowHeaderMenu(prev => !prev)}
              variant="ghost"
              size="sm"
              className="h-11 w-11 rounded-full border border-[var(--story-line)] bg-[var(--story-surface-solid)]/86 p-0 text-[var(--story-ink-soft)] shadow-[var(--story-shadow-s)] backdrop-blur-xl hover:bg-[var(--story-accent-soft)] hover:text-[var(--story-ink)] dark:bg-[#12100E]/82"
              aria-label="Interview options"
              aria-expanded={showHeaderMenu}
            >
              <Menu className="w-5 h-5" />
            </Button>

            {showHeaderMenu && (
              <div className="absolute right-0 top-full mt-2 w-56 rounded-xl border border-[var(--story-line)] bg-[var(--story-surface-solid)] shadow-xl z-[80] overflow-hidden">
                {!isComplete && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setShowHeaderMenu(false);
                        openChaptersModal();
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left text-[var(--story-ink)] hover:bg-[var(--story-accent-soft)]"
                    >
                      <BookOpen className="w-4 h-4 text-[#8B7355] dark:text-[#C4A882]" />
                      <span className="flex-1">My chapters</span>
                      {compiledChapters.length > 0 && (
                        <span className="text-xs text-stone-400">{compiledChapters.length}</span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowHeaderMenu(false);
                        handlePreviewDraft();
                      }}
                      disabled={isPreviewLoading}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left text-[var(--story-ink)] hover:bg-[var(--story-accent-soft)] disabled:opacity-50"
                    >
                      {isPreviewLoading ? <Loader2 className="w-4 h-4 animate-spin text-[#8B7355]" /> : <Sparkles className="w-4 h-4 text-[#8B7355] dark:text-[#C4A882]" />}
                      Preview draft
                    </button>
                    {progress?.section_stats?.answers >= 2 && (
                      <button
                        type="button"
                        onClick={requestSkipSection}
                        className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left text-[var(--story-ink)] hover:bg-[var(--story-accent-soft)]"
                        data-testid="skip-section-btn"
                      >
                        <ChevronRight className="w-4 h-4 text-[#8B7355] dark:text-[#C4A882]" />
                        Skip section
                      </button>
                    )}
                    <div className="h-px bg-stone-200 dark:bg-[#2A2520]" />
                  </>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setShowHeaderMenu(false);
                    onClose();
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left text-[var(--story-ink-soft)] hover:bg-[var(--story-accent-soft)]"
                >
                  <X className="w-4 h-4" />
                  Exit interview
                </button>
              </div>
            )}
          </div>
        </div>
        </div>
      </header>

      {/* Messages Area */}
      <div
        className="min-h-0 flex-1 overflow-y-auto px-4 pt-[calc(env(safe-area-inset-top,0px)+6.75rem)]"
        style={{
          paddingBottom: 'calc(15rem + env(safe-area-inset-bottom, 0px))',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain'
        }}
      >
        <div className="max-w-2xl mx-auto space-y-7">
          {messages.map((msg, idx) => (
            <MessageBubble
              key={idx}
              message={msg}
              onViewChapter={(id) => navigate(`/chapters/${id}`)}
              onEdit={msg.type === 'user' && msg.answerIndex != null
                ? (newContent) => handleEditAnswer(idx, msg.answerIndex, newContent)
                : undefined}
              onDelete={msg.type === 'user' && msg.answerIndex != null
                ? () => handleDeleteAnswer(idx, msg.answerIndex)
                : undefined}
              onContinueNextChapter={msg.type === 'chapter-created' ? () => handleContinueToNextChapter(msg) : undefined}
            />
          ))}
          
          {/* AI Thinking Indicator - Shows while processing */}
          {isAiThinking && (
            <div className="flex items-start space-x-3 animate-fadeIn">
              <div className="w-8 h-8 bg-[var(--story-primary-soft)] rounded-full flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-4 h-4 text-[var(--story-primary)] animate-pulse" />
              </div>
              <div className="rounded-[1.35rem] rounded-tl-md border border-[var(--story-line)] bg-[var(--story-surface-solid)]/74 px-4 py-3 shadow-sm backdrop-blur-sm dark:bg-[#211E1A]/64">
                <div className="flex items-center space-x-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-[var(--story-primary)] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-[var(--story-primary)] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-[var(--story-primary)] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                  <span className="text-sm italic text-[var(--story-muted)]">Thinking...</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Typing Animation - Shows AI response being typed */}
          {typingMessage && displayedText && (
            <div className="flex items-start space-x-3 animate-fadeIn">
              <div className="w-8 h-8 bg-[var(--story-primary-soft)] rounded-full flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-4 h-4 text-[var(--story-primary)]" />
              </div>
              <div className="max-w-[88%] rounded-[1.35rem] rounded-tl-md border border-[var(--story-line)] bg-[var(--story-surface-solid)]/74 px-4 py-3 shadow-sm backdrop-blur-sm dark:bg-[#211E1A]/64 sm:max-w-md">
                <p className="text-[16px] leading-7 text-[var(--story-ink-soft)]">
                  {displayedText}
                  <span className="inline-block w-0.5 h-4 bg-[var(--story-primary)] ml-0.5 animate-pulse" />
                </p>
                {/* Follow-up flows naturally after acknowledgment finishes typing */}
                {displayedText === typingMessage.content && typingMessage.followUp && (
                  <p className="mt-2 text-[16px] font-medium leading-7 text-[var(--story-ink)]">{typingMessage.followUp}</p>
                )}
              </div>
            </div>
          )}
          
          {/* Compile CTA — appears when enough content for a chapter */}
          {canCompileNow && !isComplete && !typingMessage && sectionsToCompile.length > 0 && (
            <div className="rounded-2xl border border-stone-200 bg-[#F8F5F0] px-4 py-3 dark:border-[#2A2520] dark:bg-[#1A1816]">
              <div className="flex items-start gap-3">
                <div className="relative mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#EFE8DE] dark:bg-[#2A2520]">
                  {isCompiling ? (
                    <>
                      <Feather className="h-5 w-5 text-[#8B7355] animate-bounce dark:text-[#C4A882]" style={{ animationDuration: '1.7s' }} />
                      <span className="absolute bottom-2 left-3 h-px w-5 rounded-full bg-[#8B7355]/35 dark:bg-[#C4A882]/35" />
                      <span className="absolute bottom-1.5 left-4 h-px w-3 rounded-full bg-[#8B7355]/25 dark:bg-[#C4A882]/25" />
                    </>
                  ) : (
                    <Sparkles className="h-4 w-4 text-[#8B7355] dark:text-[#C4A882]" />
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-stone-800 dark:text-stone-200">
                    {isCompiling ? 'Writing your chapter' : 'Enough material for this chapter'}
                  </div>
                  <div className="mt-1 text-sm leading-5 text-stone-600 dark:text-stone-400">
                    {isCompiling
                      ? 'Organizing your scenes, shaping the structure, and polishing the voice.'
                      : 'Preview a quick draft or create the chapter when you are ready.'}
                  </div>

                  {isCompiling && (
                    <div className="mt-3 flex items-center gap-1.5 text-xs text-stone-500 dark:text-stone-400">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#8B7355] animate-bounce dark:bg-[#C4A882]" style={{ animationDelay: '0ms' }} />
                      <span className="h-1.5 w-1.5 rounded-full bg-[#8B7355] animate-bounce dark:bg-[#C4A882]" style={{ animationDelay: '120ms' }} />
                      <span className="h-1.5 w-1.5 rounded-full bg-[#8B7355] animate-bounce dark:bg-[#C4A882]" style={{ animationDelay: '240ms' }} />
                      <span className="ml-1">This can take about a minute.</span>
                    </div>
                  )}
                </div>

                <div className="flex flex-shrink-0 items-center gap-3 pt-1">
                  {!isCompiling && (
                    <button
                      onClick={handlePreviewDraft}
                      disabled={isCompiling || isPreviewLoading}
                      className="text-sm text-stone-500 underline underline-offset-2 hover:text-stone-700 disabled:opacity-50 dark:text-stone-400 dark:hover:text-stone-200"
                    >
                      Preview
                    </button>
                  )}
                  <button
                    onClick={() => {
                      const section = sectionsToCompile[sectionsToCompile.length - 1];
                      setSectionToCompile(section);
                      setShowStyleModal(true);
                    }}
                    disabled={isCompiling}
                    className="text-sm font-medium text-[#2A2420] underline underline-offset-2 disabled:opacity-50 dark:text-[#C4A882]"
                    data-testid="compile-chapter-btn"
                  >
                    {isCompiling ? 'Creating...' : 'Create chapter →'}
                  </button>
                  {!isCompiling && (
                    <button
                      onClick={() => setCanCompileNow(false)}
                      className="text-stone-400 hover:text-stone-600 dark:text-stone-500 dark:hover:text-stone-300"
                      title="Keep interviewing"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Compilation error banner — shown after any compile failure */}
          {compilationError && !isCompiling && (
            <div className="flex items-center gap-3 py-3 px-4 rounded-2xl border border-red-200/60 dark:border-red-900/40 bg-red-50/80 dark:bg-red-900/10">
              <span className="text-sm text-red-700 dark:text-red-300 flex-1">{compilationError}</span>
              <button
                onClick={() => {
                  setCompilationError(null);
                  setShowStyleModal(true);
                }}
                className="text-sm font-medium text-red-700 dark:text-red-300 underline underline-offset-2 shrink-0"
              >
                Try again
              </button>
              <button
                onClick={() => setCompilationError(null)}
                className="text-stone-400 hover:text-stone-600 dark:text-stone-500 dark:hover:text-stone-300 shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Complete State */}
          {isComplete && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 rounded-2xl p-6 border border-green-200 dark:border-green-700 text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-semibold text-green-800 dark:text-green-200 mb-2">
                Interview Complete!
              </h3>
              <p className="text-green-700 dark:text-green-300 mb-4">
                You've shared your story beautifully. Now let's turn it into chapters.
              </p>
              {sectionsToCompile.length > 0 && (
                <div className="space-y-2 mb-4">
                  {sectionsToCompile.map(section => (
                    <Button
                      key={section}
                      onClick={() => handleCompileChapter(section)}
                      disabled={isCompiling}
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                    >
                      {isCompiling ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Sparkles className="w-4 h-4 mr-2" />
                      )}
                      Create "{getSectionLabel(section)}" Chapter
                    </Button>
                  ))}
                </div>
              )}
              <Button
                onClick={handleViewBook}
                variant="outline"
                className="border-green-600 text-green-700 hover:bg-green-50 dark:border-green-400 dark:text-green-300"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                View Your Book
              </Button>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Section label strip — thin context indicator above input */}
      {currentQuestion?.show_section_label === true && !isComplete && (
        <div className="border-t border-[var(--story-line)] px-4 py-1.5 bg-[var(--story-bg)]/88">
          <div className="max-w-2xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
                <span className="story-label shrink-0">
                {currentQuestion.section_label}
              </span>
              {(() => {
                const sections = getInterviewSections(interviewGenre).map((item) => item.key);
                const idx = sections.indexOf(currentQuestion.section);
                if (idx === -1) return null;
                const total = sections.length;
                const isLast = idx === total - 1;
                const nextLabel = !isLast ? getSectionLabel(sections[idx + 1]) : null;
                return (
                  <span className="story-meta text-[10px] truncate">
                    {isLast
                      ? '· Final section'
                      : `· ${idx + 1} of ${total} — Next: ${nextLabel}`}
                  </span>
                );
              })()}
            </div>
            <div className="flex items-center gap-1">
              {!conversationMode && (
                <button
                  onClick={handleReadQuestion}
                  className={`h-7 px-2 rounded-lg transition-colors flex items-center gap-1.5 text-[11px] font-medium ${
                    isPromptSpeakingRef.current
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                      : 'text-stone-500 hover:bg-stone-100 dark:text-stone-500 dark:hover:bg-[#2A2520]/70'
                  }`}
                  title="Read question aloud"
                >
                  <Volume2 className="w-3.5 h-3.5" />
                  Read
                </button>
              )}
              <button
                onClick={() => {
                  handleVoiceModeToggle();
                }}
                title={conversationMode ? 'Voice mode on' : 'Voice mode off'}
                className={`h-7 px-2 rounded-lg transition-colors flex items-center gap-1.5 text-[11px] font-medium ${
                  conversationMode
                    ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                    : 'text-stone-500 hover:bg-stone-100 dark:text-stone-500 dark:hover:bg-[#2A2520]/70'
                }`}
              >
                <Headphones className="w-3.5 h-3.5" />
                Voice mode
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Premium Input Area */}
      {currentQuestion && !isComplete && (
        <div
          className="pointer-events-none fixed inset-x-0 z-[70] bg-gradient-to-t from-[var(--story-bg)] via-[var(--story-bg)]/78 to-transparent px-4 pb-[calc(env(safe-area-inset-bottom,0px)+0.85rem)] pt-20"
          style={{ bottom: 0, left: 0, right: 0, paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <div className="pointer-events-auto mx-auto max-w-2xl">
            {conversationMode && (
              <div className="mb-2 flex items-center justify-between gap-3 rounded-full border border-[var(--story-line)] bg-[var(--story-surface-solid)]/90 px-3 py-2 text-xs text-[var(--story-ink-soft)] shadow-[var(--story-shadow-s)] backdrop-blur-xl dark:bg-[#171411]/90">
                <button
                  type="button"
                  onClick={handleInterruptSpeech}
                  disabled={!isPromptSpeaking}
                  aria-label={isPromptSpeaking ? 'Stop speaking and take the turn' : undefined}
                  className={`flex min-w-0 flex-1 items-center gap-2 text-left text-[var(--story-ink-soft)] disabled:opacity-100 ${
                    isPromptSpeaking ? 'cursor-pointer' : 'cursor-default'
                  }`}
                >
                  <Headphones className="h-4 w-4 shrink-0 text-[var(--story-primary)]" />
                  <span className="truncate">
                    {isPromptSpeaking
                      ? 'Speaking — tap to jump in'
                      : isTranscribing
                      ? 'Transcribing...'
                      : isListening
                      ? 'Listening...'
                      : isSending || isAiThinking
                      ? 'Thinking...'
                      : voicePausedByBackground
                      ? 'Tap mic to continue'
                      : 'Tap mic to speak'}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={handleVoiceModeToggle}
                  className="shrink-0 rounded-full px-2 py-1 text-[11px] font-medium text-[var(--story-muted)] hover:bg-[var(--story-accent-soft)] hover:text-[var(--story-ink)]"
                >
                  End
                </button>
              </div>
            )}
            
            {/* Auto-save indicator */}
            {showSaved && (
              <div className="flex justify-end mb-1">
                <span className="text-xs text-[#8B7355]/70 dark:text-[#A89070]/60">Saved ✓</span>
              </div>
            )}

            {/* Unified Input Bar */}
            <div
              className={`
                relative overflow-visible
                rounded-[1.7rem]
                border border-black/5 dark:border-white/10
                bg-[rgba(255,252,247,0.88)] dark:bg-[rgba(14,13,12,0.88)]
                shadow-[0_24px_70px_-40px_rgba(20,14,10,0.95)]
                backdrop-blur-2xl
                transition-all duration-200
                ${isListening ? 'ring-2 ring-purple-400/40' : ''}
              `}
            >
              {/* Text Input - Scrollable chat-box style */}
              <textarea
                ref={textareaRef}
                value={
                  isListening
                    ? 'Listening...'
                    : currentAnswer
                }
                readOnly={isListening}
                onChange={(e) => {
                  if (!isListening) {
                    setCurrentAnswer(e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 180) + 'px';
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && !isListening) {
                    e.preventDefault();
                    setShowComposerMenu(false);
                    handleSendAnswer();
                  }
                }}
                onFocus={() => {
                  window.setTimeout(() => {
                    messagesEndRef.current?.scrollIntoView({ block: 'end' });
                  }, 80);
                }}
                placeholder="Share your story..."
                className="block w-full bg-transparent px-5 pb-2 pt-5 !border-0 !outline-none !ring-0 !shadow-none text-[17px] leading-7 text-gray-800 placeholder:text-gray-400 focus:!outline-none focus:!ring-0 focus-visible:!outline-none focus-visible:!ring-0 dark:text-gray-100 dark:placeholder:text-gray-500 resize-none overflow-y-auto appearance-none"
                style={{
                  outline: 'none',
                  boxShadow: 'none',
                  border: 0,
                  minHeight: '66px',
                  maxHeight: '180px',
                  WebkitAppearance: 'none',
                  backgroundColor: 'transparent'
                }}
                rows={1}
                autoComplete="off"
                autoCorrect="on"
                autoCapitalize="sentences"
                spellCheck="true"
                data-testid="interview-answer-input"
              />

              <div className="flex items-center justify-between gap-2 px-2 pb-2 pt-1">
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowComposerMenu(prev => !prev)}
                    className="flex h-11 w-11 items-center justify-center rounded-full bg-black/[0.04] text-[var(--story-ink-soft)] transition-colors hover:bg-black/[0.07] hover:text-[var(--story-ink)] dark:bg-white/[0.06] dark:hover:bg-white/[0.1]"
                    title="Input options"
                    aria-label="Input options"
                    aria-expanded={showComposerMenu}
                  >
                    <Plus className="h-5 w-5" />
                  </button>

                  {showComposerMenu && (
                    <div className="absolute bottom-full left-0 mb-2 w-56 overflow-hidden rounded-2xl border border-[var(--story-line)] bg-[var(--story-surface-solid)]/95 p-1.5 shadow-2xl backdrop-blur-xl dark:bg-[#171411]/95">
                      <button
                        type="button"
                        onClick={() => {
                          setShowComposerMenu(false);
                          handleReadQuestion();
                        }}
                        className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition-colors ${
                          isPromptSpeakingRef.current
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                            : 'text-[var(--story-ink)] hover:bg-[var(--story-accent-soft)]'
                        }`}
                      >
                        <Volume2 className="h-4 w-4 shrink-0" />
                        <span className="flex-1">Read mode</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowComposerMenu(false);
                          handleVoiceModeToggle();
                        }}
                        className={`mt-1 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition-colors ${
                          conversationMode
                            ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                            : 'text-[var(--story-ink)] hover:bg-[var(--story-accent-soft)]'
                        }`}
                      >
                        <Headphones className="h-4 w-4 shrink-0" />
                        <span className="flex-1">Conversation mode</span>
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={handleVoiceInput}
                    disabled={isTranscribing}
                    className={`
                      flex h-11 w-11 shrink-0 items-center justify-center rounded-full
                      transition-colors duration-150
                      !outline-none !ring-0 !shadow-none
                      focus:!outline-none focus:!ring-0 focus-visible:!outline-none focus-visible:!ring-0
                      ${isTranscribing
                        ? 'bg-black/[0.03] text-gray-300 dark:bg-white/[0.04] dark:text-gray-600'
                        : isListening
                        ? 'bg-purple-100 text-purple-600 dark:bg-purple-900/40 dark:text-purple-400' 
                        : 'bg-black/[0.04] text-gray-500 hover:bg-black/[0.07] hover:text-gray-700 dark:bg-white/[0.06] dark:text-gray-400 dark:hover:bg-white/[0.1] dark:hover:text-gray-200'
                      }
                    `}
                    style={{ outline: 'none', boxShadow: 'none' }}
                    data-testid="interview-voice-btn"
                    title={isTranscribing ? 'Transcribing voice' : isListening ? 'Stop listening' : 'Start voice input'}
                    aria-label={isTranscribing ? 'Transcribing voice' : isListening ? 'Stop listening' : 'Start voice input'}
                  >
                    {isTranscribing ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : isListening ? (
                      <div className="flex h-4 w-5 items-end justify-center gap-[2px]">
                        {[0.6, 1.0, 0.8, 0.5].map((multiplier, i) => {
                          const level = Math.max(2, (audioLevel / 100) * 16 * multiplier);
                          return (
                            <div
                              key={i}
                              className="w-[3px] rounded-full bg-purple-500 transition-all duration-75 dark:bg-purple-400"
                              style={{ height: `${level}px` }}
                            />
                          );
                        })}
                      </div>
                    ) : (
                      <Mic className="h-5 w-5" />
                    )}
                  </button>

                  <button
                    onClick={() => {
                      setShowComposerMenu(false);
                      handleSendAnswer();
                    }}
                    disabled={(!currentAnswer.trim() && !isListening) || isSending || isTranscribing}
                    className={`
                      flex h-11 w-11 shrink-0 items-center justify-center rounded-full
                      transition-all duration-150
                      ${(currentAnswer.trim() || isListening) && !isSending && !isTranscribing
                        ? 'bg-[var(--story-primary)] text-white shadow-[0_12px_28px_-18px_rgba(79,65,93,0.9)] hover:bg-[var(--story-primary-hover)]' 
                        : 'bg-black/[0.03] text-gray-300 dark:bg-white/[0.04] dark:text-gray-600'
                      }
                    `}
                    data-testid="interview-send-btn"
                    aria-label="Send answer"
                  >
                    {isSending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ArrowUp className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Skip Nudge Modal */}
      {showSkipNudge && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[80] p-4">
          <div className="bg-white dark:bg-[#22201C] rounded-2xl max-w-sm w-full p-6 shadow-2xl border border-stone-200/50 dark:border-[#2A2520]/50">
            <h3 className="font-semibold text-gray-900 dark:text-white text-base mb-1">
              Before you skip...
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              For a richer chapter, it helps to also cover:
            </p>
            <ul className="space-y-2 mb-5">
              {missingElements.map((el) => (
                <li key={el} className="flex items-center gap-2.5 text-sm text-gray-700 dark:text-gray-300">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                  {el}
                </li>
              ))}
            </ul>
            <p className="text-xs text-gray-400 dark:text-gray-500 mb-5">
              Your chapter will still be great — it's completely your call.
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => setShowSkipNudge(false)}
                variant="outline"
                className="flex-1 dark:border-[#2A2520] dark:text-[#F0EBE4]"
              >
                Keep going
              </Button>
              <Button
                onClick={() => { setShowSkipNudge(false); handleSkipSection(); }}
                className="flex-1 bg-[#2A2420] hover:bg-[#1A1A1A] text-white dark:bg-[#F8F5F0] dark:text-[#2A2420] dark:hover:bg-white"
              >
                Skip anyway
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Style Selector Modal */}
      {/* Compiled Chapters Modal */}
      {showChaptersModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[70] p-0 sm:p-4">
          <div className="bg-white dark:bg-[#22201C] rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl shadow-2xl flex flex-col max-h-[90vh]">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-[#2A2520]/50 flex items-center justify-between flex-shrink-0">
              {selectedChapter ? (
                <button
                  onClick={() => setSelectedChapter(null)}
                  className="flex items-center gap-2 text-sm text-stone-500 hover:text-stone-800 dark:hover:text-stone-200"
                >
                  <ChevronRight className="w-4 h-4 rotate-180" />
                  All chapters
                </button>
              ) : (
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Your Chapters</h3>
              )}
              <button onClick={() => setShowChaptersModal(false)} className="text-stone-400 hover:text-stone-600 dark:hover:text-stone-200">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-6">
              {chaptersLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
                </div>
              ) : selectedChapter ? (
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <h4 className="text-xl font-semibold text-gray-900 dark:text-white">{selectedChapter.title}</h4>
                    <span className="text-xs text-stone-400">{selectedChapter.word_count?.toLocaleString()} words</span>
                  </div>
                  <div className="prose dark:prose-invert prose-sm max-w-none">
                    {selectedChapter.content.split('\n\n').map((para, i) => (
                      <p key={i} className="text-gray-800 dark:text-gray-200 leading-relaxed mb-4">{para}</p>
                    ))}
                  </div>
                </div>
              ) : compiledChapters.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
                  <BookOpen className="w-8 h-8 text-stone-300 dark:text-stone-600" />
                  <p className="text-gray-700 dark:text-gray-300 font-medium">No chapters yet</p>
                  <p className="text-sm text-stone-400 max-w-xs">Keep sharing your story. When you're ready, use the "Create chapter" button to turn your answers into a beautifully written chapter.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {compiledChapters.map(ch => (
                    <button
                      key={ch.chapter_id}
                      onClick={() => setSelectedChapter(ch)}
                      className="w-full text-left p-4 rounded-xl border border-stone-200 dark:border-[#2A2520] hover:bg-[#F8F5F0] dark:hover:bg-[#2A2520] transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900 dark:text-white">{ch.title}</span>
                        <ChevronRight className="w-4 h-4 text-stone-400" />
                      </div>
                      <p className="text-xs text-stone-400 mt-1">{ch.word_count?.toLocaleString()} words · {ch.content.slice(0, 80).trim()}…</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Draft Preview Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[70] p-0 sm:p-4">
          <div className="bg-white dark:bg-[#22201C] rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl shadow-2xl flex flex-col max-h-[90vh]">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-[#2A2520]/50 flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  {previewContent?.tooShort ? 'Keep sharing' : (previewContent?.title || (previewContent?.section_label ?? 'Generating draft…'))}
                </h3>
                {previewContent && !previewContent.tooShort && (
                  <span className="text-xs text-stone-400">
                    {previewContent.word_count?.toLocaleString()} words · draft, not saved
                  </span>
                )}
              </div>
              <button onClick={() => setShowPreviewModal(false)} className="text-stone-400 hover:text-stone-600 dark:hover:text-stone-200">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-6">
              {previewContent?.tooShort ? (
                <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
                  <Sparkles className="w-8 h-8 text-[#8B7355]" />
                  <p className="text-gray-700 dark:text-gray-300 font-medium">Share a bit more first</p>
                  <p className="text-sm text-stone-400 max-w-xs">Answer a couple more questions and come back — we'll turn what you've shared into a prose draft you can read and save.</p>
                </div>
              ) : isPreviewLoading ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <Loader2 className="w-7 h-7 animate-spin text-[#8B7355]" />
                  <p className="text-sm text-stone-400">Writing your draft…</p>
                </div>
              ) : previewContent ? (
                <div className="prose dark:prose-invert prose-sm max-w-none">
                  {/* Details the draft contains that the conversation doesn't support.
                      Advisory — the draft is never withheld over these. */}
                  {previewContent.fidelity_findings?.length > 0 && (
                    <div className="mb-5 rounded-xl border border-[#E8D8BD] bg-[#FBF4E8] px-4 py-3 dark:border-[#463724] dark:bg-[#282116]">
                      <p className="mb-2 text-sm font-semibold text-[#6F4A1D] dark:text-[#E7C68D]">
                        Worth checking — {previewContent.fidelity_findings.length === 1
                          ? 'one detail we couldn’t find in what you told us'
                          : `${previewContent.fidelity_findings.length} details we couldn’t find in what you told us`}
                      </p>
                      <ul className="m-0 list-none space-y-2 p-0">
                        {previewContent.fidelity_findings.map((f, i) => (
                          <li key={i} className="m-0 text-sm text-[#6F4A1D]/90 dark:text-[#E7C68D]/90">
                            <span className="italic">&ldquo;{f.quote}&rdquo;</span>
                            {f.note && <span className="block text-xs opacity-80">{f.note}</span>}
                          </li>
                        ))}
                      </ul>
                      <p className="mt-2 mb-0 text-xs text-[#8C6A3A] dark:text-[#D7BB88]">
                        If any of these are wrong, mention it and we&rsquo;ll write it again.
                      </p>
                    </div>
                  )}
                  {previewContent.content.split('\n\n').map((para, i) => (
                    <p key={i} className="text-gray-800 dark:text-gray-200 leading-relaxed mb-4">{para}</p>
                  ))}
                </div>
              ) : null}
            </div>
            {previewContent && !previewContent.tooShort && (
              <div className="px-6 py-4 border-t border-gray-200 dark:border-[#2A2520]/50 flex justify-between items-center gap-3 flex-shrink-0">
                <button
                  onClick={handleRegenerateDraft}
                  disabled={isPreviewLoading || isPromoting}
                  className="text-sm text-stone-500 hover:text-stone-800 dark:hover:text-stone-200 disabled:opacity-40"
                >
                  Regenerate
                </button>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowPreviewModal(false)}
                    className="text-sm text-stone-500 hover:text-stone-800 dark:hover:text-stone-200"
                  >
                    Keep talking
                  </button>
                  <Button
                    onClick={() => handlePromoteDraft(false)}
                    disabled={isPromoting || isPreviewLoading}
                    className="bg-[#2A2420] dark:bg-[#C4A882] text-white dark:text-[#2A2420] hover:opacity-90 disabled:opacity-50"
                    size="sm"
                  >
                    {isPromoting ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-2" />Save as chapter</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showStaleModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[80] p-4">
          <div className="bg-white dark:bg-[#22201C] rounded-2xl max-w-sm w-full shadow-2xl p-6 text-center">
            <p className="text-base font-semibold text-gray-900 dark:text-white mb-2">
              Your answers have changed
            </p>
            <p className="text-sm text-stone-500 dark:text-stone-400 mb-6">
              You've added more answers since this draft was generated. Save this version anyway, or regenerate with your latest answers?
            </p>
            <div className="flex flex-col gap-2">
              <Button
                onClick={handleRegenerateDraft}
                className="w-full bg-[#2A2420] dark:bg-[#C4A882] text-white dark:text-[#2A2420] hover:opacity-90"
                size="sm"
              >
                Regenerate with latest answers
              </Button>
              <button
                onClick={() => handlePromoteDraft(true)}
                className="text-sm text-stone-500 hover:text-stone-800 dark:hover:text-stone-200 py-2"
              >
                Save this version anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {showStyleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white dark:bg-[#22201C] rounded-2xl max-w-lg w-full shadow-2xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-[#2A2520]/50">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                Choose Compilation Style
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                How should we turn your answers into a chapter?
              </p>
            </div>

            {/* Style Options */}
            <div className="p-6 space-y-3">
              {compilationStyles.map((style) => (
                <button
                  key={style.id}
                  onClick={() => setSelectedStyle(style.id)}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                    selectedStyle === style.id
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                      : 'border-gray-200 dark:border-[#2A2520]/50 hover:border-purple-300 dark:hover:border-purple-700'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {style.name}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {style.description}
                      </p>
                    </div>
                    {selectedStyle === style.id && (
                      <Check className="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0 ml-3" />
                    )}
                  </div>
                </button>
              ))}
            </div>

            {/* Actions */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-[#1A1816]/50 rounded-b-2xl flex items-center justify-end space-x-3">
              <Button
                onClick={() => {
                  setShowStyleModal(false);
                  setSectionToCompile(null);
                }}
                variant="outline"
                disabled={isCompiling}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmCompileChapter}
                disabled={isCompiling}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                {isCompiling ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Compiling...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Compile Chapter
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Message Bubble Component
function MessageBubble({ message, onViewChapter, onEdit, onDelete, onContinueNextChapter }) {
  const { type, content, icon, followUp, chapterId, section, sectionLabel, nextQuestion, nextQuestionLoading } = message;
  const [isEditing, setIsEditing] = React.useState(false);
  const [editText, setEditText] = React.useState(content);
  const [hovered, setHovered] = React.useState(false);
  
  if (type === 'system') {
    return (
      <div className="flex items-start space-x-3">
        <div className="w-10 h-10 bg-gradient-to-br from-stone-200 to-stone-200 dark:from-stone-900/30 dark:to-stone-700/30 rounded-full flex items-center justify-center flex-shrink-0">
          {icon === 'coffee' ? (
            <Coffee className="w-5 h-5 text-stone-700 dark:text-stone-500" />
          ) : (
            <BookOpen className="w-5 h-5 text-stone-700 dark:text-stone-500" />
          )}
        </div>
        <div className="bg-stone-100 dark:bg-stone-800/20 rounded-2xl rounded-tl-none px-4 py-3 max-w-md">
          <p className="text-gray-700 dark:text-gray-200 leading-relaxed">{content}</p>
        </div>
      </div>
    );
  }
  
  if (type === 'user') {
    const saveEdit = () => {
      const trimmed = editText.trim();
      if (trimmed && trimmed !== content && onEdit) onEdit(trimmed);
      setIsEditing(false);
    };
    return (
      <div
        className="flex justify-end items-end gap-2"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {/* Edit/Delete controls — visible on hover */}
        {(hovered || isEditing) && !isEditing && (
          <div className="flex items-center gap-1 mb-1">
            {onEdit && (
              <button
                onClick={() => { setEditText(content); setIsEditing(true); }}
                className="p-1 rounded text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 hover:bg-stone-100 dark:hover:bg-[#2A2520] transition-colors"
                title="Edit"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
            {onDelete && (
              <button
                onClick={onDelete}
                className="p-1 rounded text-stone-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                title="Delete"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
        <div className="max-w-[84%] rounded-[1.35rem] rounded-tr-md bg-[var(--story-primary)] px-4 py-3 shadow-[var(--story-shadow-s)] sm:max-w-md">
          {isEditing ? (
            <div className="flex flex-col gap-2">
              <textarea
                autoFocus
                value={editText}
                onChange={e => setEditText(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEdit(); }
                  if (e.key === 'Escape') setIsEditing(false);
                }}
                className="w-full bg-white/20 text-white placeholder:text-white/60 rounded-lg px-2 py-1 text-sm resize-none outline-none min-h-[60px]"
                rows={3}
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setIsEditing(false)}
                  className="text-xs text-white/70 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={saveEdit}
                  className="text-xs bg-white/20 hover:bg-white/30 text-white px-2 py-0.5 rounded"
                >
                  Save
                </button>
              </div>
            </div>
          ) : (
            <p className="text-[16px] leading-7 text-white">{content}</p>
          )}
        </div>
      </div>
    );
  }
  
  // NEW: Handle AI questions (shown in conversation history)
  if (type === 'ai-question') {
    return (
      <div className="flex items-start space-x-3">
        <div className="w-8 h-8 bg-[var(--story-primary-soft)] rounded-full flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4 text-[var(--story-primary)]" />
        </div>
        <div className="space-y-2 max-w-[88%] sm:max-w-md">
          {sectionLabel && (
            <span className="story-pill inline-flex border-0 bg-[var(--story-accent-soft)]/70 px-2 py-1 text-[10px]">
              {sectionLabel}
            </span>
          )}
          <div className="rounded-[1.35rem] rounded-tl-md border border-[var(--story-line)] bg-[var(--story-surface-solid)]/74 px-4 py-3 shadow-sm backdrop-blur-sm dark:bg-[#211E1A]/64">
            <p className="text-[16px] font-medium leading-7 text-[var(--story-ink-soft)]">{content}</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (type === 'ai') {
    return (
      <div className="flex items-start space-x-3">
        <div className="w-8 h-8 bg-[var(--story-primary-soft)] rounded-full flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4 text-[var(--story-primary)]" />
        </div>
        <div className="max-w-[88%] sm:max-w-md">
          {/* Single unified message box with acknowledgment + question */}
          <div className="rounded-[1.35rem] rounded-tl-md border border-[var(--story-line)] bg-[var(--story-surface-solid)]/74 px-4 py-3 shadow-sm backdrop-blur-sm dark:bg-[#211E1A]/64">
            {/* Acknowledgment */}
            <p className="text-[16px] leading-7 text-[var(--story-ink-soft)]">{content}</p>
            
            {/* Follow-up question flows naturally from acknowledgment */}
            {followUp && (
              <p className="mt-2 text-[16px] font-medium leading-7 text-[var(--story-ink)]">{followUp}</p>
            )}
            
          </div>
        </div>
      </div>
    );
  }
  
  if (type === 'pause-offer') {
    return (
      <div className="flex justify-center">
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl px-4 py-3 border border-amber-200 dark:border-amber-700 max-w-sm text-center">
          <p className="text-sm text-amber-700 dark:text-amber-300">{content}</p>
          <p className="text-xs text-amber-600/70 dark:text-amber-400/70 mt-1">
            (You can skip to the next section anytime using the Skip button)
          </p>
        </div>
      </div>
    );
  }
  
  if (type === 'section-skip') {
    return (
      <div className="flex justify-center">
        <div className="bg-stone-100 dark:bg-stone-800/30 rounded-full px-4 py-2 flex items-center space-x-2">
          <ChevronRight className="w-4 h-4 text-stone-600 dark:text-stone-400" />
          <span className="text-sm text-stone-600 dark:text-stone-400">{content}</span>
        </div>
      </div>
    );
  }
  
  if (type === 'milestone') {
    return (
      <div className="flex justify-center">
        <div className="bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-800/30 dark:to-emerald-800/30 rounded-full px-4 py-2 flex items-center space-x-2">
          <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
          <span className="text-sm text-green-700 dark:text-green-300 font-medium">{content}</span>
        </div>
      </div>
    );
  }
  
  if (type === 'section-intro') {
    return (
      <div className="flex justify-center my-4">
        <div className="bg-gradient-to-r from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 rounded-xl px-5 py-5 max-w-md text-center border border-purple-200/50 dark:border-purple-700/50 shadow-sm">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-violet-600 rounded-full flex items-center justify-center mx-auto mb-3">
            <BookOpen className="w-5 h-5 text-white" />
          </div>
          <div className="text-xs font-semibold text-purple-500 dark:text-purple-400 uppercase tracking-wide mb-2">
            {sectionLabel || 'Next Chapter'}
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">{content}</p>
        </div>
      </div>
    );
  }
  
  if (type === 'chapter-created') {
    return (
      <div className="flex justify-center">
        <div className="bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-800/30 dark:to-pink-800/30 rounded-2xl px-5 py-4 text-center max-w-sm border border-purple-200 dark:border-purple-700">
          <Sparkles className="w-6 h-6 text-purple-500 mx-auto mb-2" />
          <p className="text-purple-800 dark:text-purple-200 font-medium mb-2">{content}</p>
          <p className="text-purple-600 dark:text-purple-300 text-sm mb-3">
            Tap continue when you are ready for the next question.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
            {chapterId && (
              <Button
                onClick={() => onViewChapter(chapterId)}
                variant="outline"
                size="sm"
                className="border-purple-300 text-purple-700 hover:bg-purple-50 dark:border-purple-600 dark:text-purple-300"
              >
                <BookOpen className="w-4 h-4 mr-1" />
                View Chapter
              </Button>
            )}
            {onContinueNextChapter && (
              <Button
                onClick={onContinueNextChapter}
                disabled={!nextQuestion || nextQuestionLoading}
                size="sm"
                className="bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-60 dark:bg-purple-500 dark:hover:bg-purple-400"
              >
                {nextQuestionLoading ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <ArrowRight className="w-4 h-4 mr-1" />
                )}
                {nextQuestionLoading ? 'Preparing Next' : 'Continue to Next Chapter'}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  if (type === 'complete') {
    return (
      <div className="flex items-start space-x-3">
        <div className="w-10 h-10 bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-800/30 dark:to-emerald-800/30 rounded-full flex items-center justify-center flex-shrink-0">
          <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
        </div>
        <div className="bg-green-50 dark:bg-green-900/20 rounded-2xl rounded-tl-none px-4 py-3 max-w-md border border-green-200 dark:border-green-700">
          <p className="text-green-800 dark:text-green-200 leading-relaxed">{content}</p>
        </div>
      </div>
    );
  }
  
  return null;
}

// Helper function
function getSectionLabel(section) {
  return getInterviewSectionLabel(section);
}

export default InterviewMode;
